import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { registerFinanceScreen } from '@/lib/finance-topics';
import { FinanceNav } from '@/components/tracking/FinanceNav';
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { AnimatePresence, motion } from 'framer-motion';
import { catchAllForClassification } from '@/lib/setup-ledger';
import { decidedAgainstSignals } from '@/lib/xero-signals';
import {
  AlertTriangle,
  ArrowRight,
  BarChart3,
  Building2,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  CircleDashed,
  Cpu,
  DownloadCloud,
  HelpCircle,
  Info,
  Landmark,
  Layers,
  Loader2,
  Ruler,
  ShieldAlert,
  ShieldCheck,
  Unlink,
} from 'lucide-react';
import { isXeroEnabled } from '@/lib/feature-flags';
import { FinanceGuard } from '@/components/finance/FinanceGuard';
import { figuresCoverLabel, formatDateShort } from '@/lib/dates';
import {
  applyStagedAnswers,
  type BlockedMetric,
  blockedMetrics,
  METRIC_REQUIREMENTS,
  metricFixPlan,
  readyMetrics,
  roleLabel,
  roleLabelShort,
  stagedCoverageDelta,
} from '@/lib/financial-calc/metric-requirements';
import { SyncStatusCluster } from '@/components/tracking/SyncStatusCluster';
import { XeroHealthPanel } from '@/components/tracking/XeroHealthPanel';
import { BankAccountsPicker, UnplacedActivity } from '@/components/tracking/BankAccountsPicker';
import { BooksDeclarations, parseHealthDeclarations } from '@/components/tracking/BooksDeclarations';
import { BooksQuestionFlow } from '@/components/tracking/BooksQuestionFlow';
import { parseHealthAcks, partitionHealth } from '@/lib/xero-health-view';
import type { XeroCashDesignation } from '@/api/xero';
import { XeroFixHint } from '@/components/tracking/OpenInXero';
import type { AccountRole } from '@/types/tracking-view';
import GlassPanel from '@/components/shared/GlassPanel';
import HearthstoneIcon from '@/components/icons/HearthstoneIcon';
import { AccountingView } from '@/components/finance/AccountingView';
import { resolveProposals } from '@/lib/setup-ledger';
import { ChapterRail } from '@/components/finance/ChapterRail';
import { buildChapters } from '@/lib/xero-chapters';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { AlfieRadialHub } from '@/components/alfie/AlfieRadialHub';
import { cn } from '@/lib/utils';
import { useCurrentOrganization } from '@/hooks/use-bss-organizations';
import { useOrgRole } from '@/hooks/use-org-role';
import {
  type MappingStage,
  useConfirmXeroMappings,
  useDisconnectXero,
  useReverseXeroCorrection,
  useSyncXeroProfitAndLoss,
  useXeroMappingFlow,
  useXeroStatus,
  useDeclareXeroHealth,
} from '@/hooks/use-xero';
import { CorrectionDialog } from '@/components/tracking/CorrectionDialog';
import type { Contamination } from '@/types/tracking-view';
import type {
  AutoResolveUpgrade,
  XeroChartRow,
  XeroConnection,
  XeroHealthReport,
  XeroMappingConfirmation,
  XeroSnapshot,
  XeroUnlockCard,
} from '@/api/xero';

/**
 * "Help Alfie unlock your metrics" — the CoA -> role mapping wizard.
 *
 * Loads in visible stages (retrieval -> programmatic classification -> AI), then
 * runs the builder through the accounts Alfie couldn't resolve, one at a time.
 * Everything confident is already mapped; resolved classifications sit in the
 * editable collapse below so any of them can be corrected too.
 */
export const Route = createFileRoute('/finance/xero/mapping')({
  // ?fix=<metricId> — arrive focused: the completed dashboard opens that metric's fixer + scrolls
  // (cockpit grey metrics). ?fixAccount=<accountGuid> — arrive on a specific ACCOUNT: expand the
  // classified table, scroll + highlight the row, open the correction dialog if it's flagged
  // (contamination "Review" / unmapped tie-out links). Independent params, consumed independently.
  validateSearch: (search: Record<string, unknown>): { fix?: string; fixAccount?: string; audit?: boolean } => ({
    fix: typeof search.fix === 'string' ? search.fix : undefined,
    fixAccount: typeof search.fixAccount === 'string' ? search.fixAccount : undefined,
    // ?audit=1 — the cockpit's "N findings to review": open the audit and scroll to it.
    audit: [true, 1, '1', 'true'].includes(search.audit as never) ? true : undefined,
  }),
  beforeLoad: () => {
    if (!isXeroEnabled()) throw redirect({ to: '/finance', replace: true });
  },
  component: () => (
    <FinanceGuard>
      <XeroMappingPage />
    </FinanceGuard>
  ),
});

/** A builder's answer to a card, a chart edit, or a group acceptance. */
interface Answer {
  role: string | null;
  signalHash: string;
  /** How it was given: default 'deliberate'; 'bulk_accept' = approved as a group. */
  state?: 'deliberate' | 'bulk_accept';
}

const FLAG_META = {
  conflict: { icon: AlertTriangle, tone: 'text-danger', label: 'Looks mis-set in Xero' },
  thin: { icon: HelpCircle, tone: 'text-warning', label: 'Needs your call' },
  coarse: { icon: Layers, tone: 'text-info', label: 'Covers several roles' },
} as const;

/** Per-flag "what could be wrong here" — real assumptions, on screen. */
function assumptionFor(card: XeroUnlockCard): string {
  switch (card.flag) {
    case 'conflict':
      return "Flagged by account audit: this account's Xero type and its report code disagree, so one of them is mis-set — which means a figure is likely wrong. Alfie surfaces it here; correct the account's type in Xero to clear it.";
    case 'coarse':
      return 'This one account covers several roles, so Alfie can classify it but not split it here. Split it in Xero if you want finer detail.';
    default:
      return 'Alfie inferred this from the account name because the type and report code were too general. Names can mislead — confirm it reads right to you.';
  }
}

/* -------------------------------------------------------------------------- */
/* Loading — visible stages                                                   */
/* -------------------------------------------------------------------------- */

type StageStatus = 'pending' | 'active' | 'done' | 'skipped';

function StageRow({
  icon,
  label,
  sub,
  status,
}: {
  icon: ReactNode;
  label: string;
  sub: string;
  status: StageStatus;
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-xl px-3 py-3 transition-colors',
        status === 'active' ? 'bg-card/70' : 'bg-transparent',
      )}
    >
      <div className="relative flex h-9 w-9 shrink-0 items-center justify-center">
        {status === 'active'
          ? <Loader2 className="h-5 w-5 animate-spin text-primary" />
          : status === 'done'
          ? <CheckCircle2 className="h-6 w-6 text-success" />
          : status === 'skipped'
          ? <CheckCircle2 className="h-6 w-6 text-muted-foreground/40" />
          : <CircleDashed className="h-5 w-5 text-muted-foreground/50" />}
      </div>
      <div
        className={cn(
          'shrink-0',
          status === 'pending' ? 'text-muted-foreground/50' : 'text-foreground',
        )}
      >
        {icon}
      </div>
      <div className="min-w-0">
        <p
          className={cn(
            'text-sm font-medium',
            status === 'pending' ? 'text-muted-foreground/60' : 'text-foreground',
          )}
        >
          {label}
        </p>
        <p className="text-xs text-muted-foreground">{status === 'skipped' ? 'Not needed' : sub}</p>
      </div>
    </div>
  );
}

function LoadingStages(
  { stage, aiInputsCount }: { stage: MappingStage; aiInputsCount: number | null },
) {
  const order: MappingStage[] = ['retrieval', 'classifying', 'ai', 'ready'];
  const at = order.indexOf(stage);
  const statusOf = (s: MappingStage): StageStatus => {
    const i = order.indexOf(s);
    if (i < at) return 'done';
    if (i === at) return 'active';
    return 'pending';
  };
  // AI row: once classify has resolved (aiInputsCount known), 0 = skipped;
  // otherwise the normal pending -> active -> done. `null` = classify not back
  // yet, so it stays pending — never a premature "Not needed".
  const aiStatus: StageStatus = aiInputsCount === 0 ? 'skipped' : statusOf('ai');

  return (
    <GlassPanel intensity="medium" rounded="2xl" className="mt-4 p-3 sm:p-4">
      <StageRow
        icon={<DownloadCloud className="h-5 w-5" />}
        label="Account retrieval"
        sub="Reading your chart of accounts from Xero"
        status={statusOf('retrieval')}
      />
      <StageRow
        icon={<Cpu className="h-5 w-5" />}
        label="Programmatic classification"
        sub="Alfie's rules place each account by type & report code"
        status={statusOf('classifying')}
      />
      <div className="relative">
        <StageRow
          icon={<AlfieRadialHub size={20} />}
          label="Alfie classification"
          sub="Reading the names Alfie's rules couldn't place"
          status={aiStatus}
        />
        {aiStatus === 'active' && (
          <HearthstoneIcon size={16} className="absolute right-4 top-4 animate-pulse" />
        )}
      </div>
    </GlassPanel>
  );
}

/* -------------------------------------------------------------------------- */
/* One unlock card                                                            */
/* -------------------------------------------------------------------------- */

function ProvenanceTag({ card }: { card: XeroUnlockCard }) {
  const isAi = card.source === 'ai';
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs',
        isAi
          ? 'border-primary/40 bg-primary/10 text-foreground'
          : 'border-border bg-card/60 text-muted-foreground',
      )}
    >
      {isAi ? <AlfieRadialHub size={12} /> : <Ruler className="h-3 w-3" />}
      {isAi ? 'Alfie read the name' : "Alfie's rules"}
    </span>
  );
}

/**
 * The two-step conflict resolver. Step 1 offers the two sections the type and
 * code disagree between. Picking the TYPE's section drills (outward animation)
 * to the band-locked roles — resolve in-app. Picking the code's section drills
 * to fix-in-Xero, because reclassifying here would break the tie-out against
 * Xero's own subtotals. Picking a role, or acknowledging the fix, is terminal →
 * the wizard swipes on.
 */
export function ConflictDrill({
  card,
  onAnswer,
  shortCode,
}: {
  card: XeroUnlockCard;
  onAnswer: (role: string | null) => void;
  shortCode: string | null;
}) {
  // 'type' = the section matching the account type (drills to roles); 'code' =
  // the report-code's section (routes to fix-in-Xero).
  const [side, setSide] = useState<'type' | 'code' | null>(null);

  return (
    <div className="mt-4">
      <AnimatePresence mode="wait">
        {side === null
          ? (
            <motion.div
              key="direction"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.18 }}
              className="flex flex-wrap gap-2"
            >
              {([['type', card.typeSectionLabel], ['code', card.codeSectionLabel]] as const).map(
                ([s, label]) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setSide(s)}
                    className="min-h-[44px] rounded-full border border-border bg-card/60 px-4 py-2 text-sm text-foreground transition-colors hover:border-primary/40"
                  >
                    {label}
                  </button>
                ),
              )}
            </motion.div>
          )
          : (
            <motion.div
              key={`drill-${side}`}
              initial={{ opacity: 0, scale: 0.96, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.22 }}
            >
              <button
                type="button"
                onClick={() => setSide(null)}
                className="mb-2 inline-flex min-h-[36px] items-center gap-1 py-1 text-xs text-muted-foreground hover:text-foreground"
              >
                <ChevronLeft className="h-3.5 w-3.5" /> Back
              </button>
              {side === 'type'
                ? (
                  <>
                    <p className="mb-2 text-sm text-foreground">
                      {card.typeSectionLabel} — which best describes it?
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {(card.drillRoles ?? []).map((opt, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => onAnswer(opt.role)}
                          className="min-h-[44px] rounded-full border border-border bg-card/60 px-4 py-2 text-sm text-foreground transition-colors hover:border-primary/40"
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </>
                )
                : (
                  <>
                    <p className="mb-2 text-sm text-foreground">
                      Then its account type is wrong in Xero — it's typed as{' '}
                      <span className="lowercase">{card.typeSectionLabel}</span>, but you've told me
                      it's <span className="lowercase">{card.codeSectionLabel}</span>. Changing it
                      here would stop your figures matching Xero's own totals, so fix the type in
                      Xero.
                    </p>
                    <XeroFixHint
                      kind="conflict"
                      shortCode={shortCode}
                      accountLabel={card.code ? `account ${card.code}` : undefined}
                    />
                    <button
                      type="button"
                      onClick={() => onAnswer(null)}
                      className="mt-3 min-h-[44px] rounded-2xl bg-primary px-5 text-sm text-primary-foreground transition-colors hover:bg-primary/90"
                    >
                      Got it — I&apos;ll fix it in Xero
                    </button>
                  </>
                )}
            </motion.div>
          )}
      </AnimatePresence>
    </div>
  );
}

function UnlockCardView({
  card,
  answer,
  onAnswer,
  shortCode,
}: {
  card: XeroUnlockCard;
  answer: Answer | undefined;
  onAnswer: (role: string | null) => void;
  shortCode: string | null;
}) {
  const [showWhy, setShowWhy] = useState(false);
  const meta = FLAG_META[card.flag];
  const Icon = meta.icon;
  // A P&L conflict with a drill resolves in two steps (money in/out → roles or
  // fix); it owns its own fix-hint, so suppress the outer one. Answered drill
  // cards (seen only via Back) fall to a compact summary.
  const isDrill = card.flag === 'conflict' && !!card.drillRoles &&
    !!card.typeSectionLabel && !!card.codeSectionLabel;
  const answeredDrill = isDrill && answer !== undefined;
  const fixKind = isDrill
    ? null
    : answer !== undefined && answer.role === null
    ? ('escape' as const)
    : card.flag === 'conflict' || card.flag === 'coarse'
    ? card.flag
    : null;

  return (
    <GlassPanel intensity="medium" rounded="2xl" className="p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className={cn('inline-flex items-center gap-1.5 text-xs font-medium', meta.tone)}>
          <Icon className="h-4 w-4" /> {meta.label}
        </span>
        <ProvenanceTag card={card} />
      </div>

      <p className="mt-3 text-lg font-medium leading-snug text-foreground">{card.question}</p>

      {answeredDrill
        ? (
          <p className="mt-4 inline-flex items-center gap-1.5 text-sm text-success">
            <Check className="h-4 w-4" aria-hidden />
            {answer && answer.role ? 'Role set — save to apply.' : "You'll fix this one in Xero."}
          </p>
        )
        : isDrill
        ? <ConflictDrill card={card} onAnswer={onAnswer} shortCode={shortCode} />
        : (
          <div className="mt-4 flex flex-wrap gap-2">
            {card.options.map((opt, i) => {
              const selected = answer !== undefined && answer.role === opt.role;
              const suggested = !answer && card.flag !== 'conflict' && opt.role === card.suggested;
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => onAnswer(opt.role)}
                  className={cn(
                    'min-h-[44px] rounded-full border px-4 py-2 text-sm transition-colors',
                    selected
                      ? 'border-primary bg-primary text-primary-foreground'
                      : suggested
                      ? 'border-primary/60 bg-primary/10 text-foreground'
                      : 'border-border bg-card/60 text-foreground hover:border-primary/40',
                  )}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        )}

      {/* Why these choices — the transparency Luke asked for, on screen.
          Hidden on the drill (it describes role options, not the money step). */}
      {!isDrill && !answeredDrill && (
      <>
      <button
        type="button"
        onClick={() => setShowWhy((s) => !s)}
        className="mt-2 inline-flex min-h-[40px] items-center gap-1.5 py-2 text-xs text-muted-foreground hover:text-foreground"
      >
        <Info className="h-3.5 w-3.5" />
        Why these choices?
      </button>
      {showWhy && (
        <div className="mt-2 space-y-2 rounded-xl border border-border bg-card/40 p-3 text-xs leading-relaxed text-muted-foreground">
          <p>
            <span className="font-medium text-foreground">The options</span> are Alfie's fixed roles
            for <span className="capitalize">{card.classification.replace(/-/g, ' ')}</span> — never
            invented, always inside where Xero's account type already puts it.
          </p>
          <p>
            <span className="font-medium text-foreground">The highlighted one</span> is{' '}
            {card.source === 'ai'
              ? 'what Alfie read from the account name (a suggestion — confirm it).'
              : "set by Alfie's rules from the account type and report code."}{' '}
            {card.reason}
          </p>
          <p>
            <span className="font-medium text-foreground">Worth knowing:</span> {assumptionFor(card)}
          </p>
        </div>
      )}
      </>
      )}

      {/* The Xero-side fix — signposted deep link, builder does it themselves. */}
      {fixKind && (
        <div className="mt-3 border-t border-border/60 pt-3">
          <XeroFixHint
            kind={fixKind}
            shortCode={shortCode}
            accountLabel={card.code ? `account ${card.code}` : undefined}
          />
          {/* A conflict with no drill (BS/equity — no in/out axis, or an AI
              conflict) has no role to pick, so it needs its OWN terminal or the
              wizard can't advance past it.

              IT USED TO SAVE NOTHING — the comment here said so: "role null, saves nothing". That
              was the whole defect. The answer was dropped before the save, the screen reported
              "Nothing to save here", and because the server's sticky gate needs a truthy role the
              same card returned on every classify for ever. It now parks on the group's catch-all,
              which allocates the money without claiming a specific role, and `deferred` is what
              records that it is a holding position rather than a call. */}
          {card.flag === 'conflict' && card.options.length === 0 && answer === undefined && (
            <button
              type="button"
              onClick={() => onAnswer(catchAllForClassification(card.classification))}
              className="mt-3 min-h-[44px] rounded-2xl bg-primary px-5 text-sm text-primary-foreground transition-colors hover:bg-primary/90"
            >
              Got it — I&apos;ll fix it in Xero
            </button>
          )}
        </div>
      )}
    </GlassPanel>
  );
}

/* -------------------------------------------------------------------------- */
/* The editable resolved chart (collapse)                                     */
/* -------------------------------------------------------------------------- */

function ResolvedChart({
  chart,
  answers,
  onEdit,
  done = false,
  contaminationsByAccount,
  correctedAccountIds,
  organizationId,
  focusAccount = null,
  open: openProp,
  onOpenChange,
}: {
  chart: XeroChartRow[];
  answers: Record<string, Answer>;
  onEdit: (row: XeroChartRow, role: string | null) => void;
  /** Completed-state header: a green tick + "Classified accounts — all N sorted". */
  done?: boolean;
  /** Account-audit findings by accountId — drive the "Correct it here" affordance. */
  contaminationsByAccount?: Map<string, Contamination>;
  /** Accounts with an active correction already applied — offer "undo" instead. */
  correctedAccountIds?: Set<string>;
  organizationId?: string | null;
  /** ?fixAccount= target — expand, scroll + highlight its row, open the correction dialog if flagged. */
  focusAccount?: string | null;
  /** Accordion-controlled (both provided); falls back to internal state elsewhere. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const [openInternal, setOpenInternal] = useState(false);
  const open = openProp ?? openInternal;
  const setOpen = (v: boolean | ((s: boolean) => boolean)) => {
    const next = typeof v === 'function' ? v(open) : v;
    if (onOpenChange) onOpenChange(next);
    else setOpenInternal(next);
  };
  const [correcting, setCorrecting] = useState<{ contamination: Contamination; name: string; signalHash: string } | null>(null);
  const reverse = useReverseXeroCorrection(organizationId ?? null);
  const focusRowRef = useRef<HTMLTableRowElement>(null);
  const dialogFiredRef = useRef<string | null>(null);

  // Arrive on a specific account (from ?fixAccount): expand the collapse ONCE (the row is hidden
  // while collapsed → a scroll would no-op). Kept separate from the dialog effect so a later
  // snapshot re-hydration can't re-open a collapse the builder has since closed.
  useEffect(() => {
    if (focusAccount) setOpen(true);
  }, [focusAccount]);

  // Open the correction dialog once the flagged account's finding is AVAILABLE — handles the
  // snapshot hydrating a tick after arrival. Fire-once per account (guard) so it never re-pops.
  useEffect(() => {
    if (!focusAccount || !organizationId || dialogFiredRef.current === focusAccount) return;
    const contam = contaminationsByAccount?.get(focusAccount);
    if (!contam || correctedAccountIds?.has(focusAccount)) return;
    const row = chart.find((r) => r.accountId === focusAccount);
    if (row) {
      setCorrecting({ contamination: contam, name: row.name, signalHash: row.signalHash });
      dialogFiredRef.current = focusAccount;
    }
  }, [focusAccount, contaminationsByAccount, correctedAccountIds, chart, organizationId]);

  // Scroll the highlighted row into view once it's rendered (after the collapse opens).
  useEffect(() => {
    if (open && focusAccount && focusRowRef.current) {
      focusRowRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [open, focusAccount]);
  return (
    <div className={done ? '' : 'mt-4'}>
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className={cn(
          'flex w-full items-center gap-2 rounded-xl px-1 py-2 text-sm hover:text-foreground',
          done ? 'text-foreground' : 'text-muted-foreground',
        )}
      >
        {done
          ? <CheckCircle2 className="h-5 w-5 text-success" />
          : <ChevronDown className={cn('h-4 w-4 transition-transform', open && 'rotate-180')} />}
        {done
          ? (
            <>
              <span className="font-medium">Classified accounts</span>
              <span className="text-muted-foreground">— all {chart.length} sorted</span>
              <ChevronDown
                className={cn('ml-auto h-4 w-4 text-muted-foreground transition-transform', open && 'rotate-180')}
              />
            </>
          )
          : <>All {chart.length} accounts — classification &amp; role</>}
      </button>
      {open && (
        <GlassPanel intensity="light" rounded="2xl" className="mt-1 overflow-x-auto p-1">
          <table className="w-full min-w-[34rem] text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-foreground">
                <th className="px-3 py-2 font-medium">Account</th>
                <th className="px-3 py-2 font-medium">Classification</th>
                <th className="px-3 py-2 font-medium">Role</th>
              </tr>
            </thead>
            <tbody>
              {chart.map((row) => {
                const edited = answers[row.accountId];
                const value = edited?.role ?? row.role ?? '';
                const isFocus = row.accountId === focusAccount;
                return (
                  <tr
                    key={row.accountId}
                    ref={isFocus ? focusRowRef : undefined}
                    className={cn('border-t border-border/60', isFocus && 'bg-warning-light/50')}
                  >
                    <td className="px-3 py-2 text-foreground">
                      <span className="text-muted-foreground">{row.code}</span> {row.name}
                      {/* A role held that's implausible for the account's Xero
                          type — likely confirmed before plausibility existed.
                          Flagged, never auto-remapped (advisor #5). */}
                      {row.roleImplausible && !edited && (
                        <span
                          className="ml-1.5 inline-flex items-center gap-1 text-xs text-warning"
                          title="This role doesn't fit the account's Xero type — worth a second look."
                        >
                          <AlertTriangle className="h-3 w-3" aria-hidden /> check this
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">
                      <span className="capitalize">{row.classification?.replace(/-/g, ' ') ?? '—'}</span>
                      {(() => {
                        const contam = contaminationsByAccount?.get(row.accountId);
                        const corrected = correctedAccountIds?.has(row.accountId);
                        if (corrected) {
                          return (
                            <span className="ml-1.5 inline-flex items-center gap-1 text-xs text-success">
                              corrected
                              {organizationId && (
                                <button
                                  type="button"
                                  onClick={() => reverse.mutate(row.accountId)}
                                  disabled={reverse.isPending}
                                  className="font-semibold text-primary hover:underline"
                                >
                                  undo
                                </button>
                              )}
                            </span>
                          );
                        }
                        if (contam && organizationId) {
                          return (
                            <button
                              type="button"
                              onClick={() =>
                                setCorrecting({ contamination: contam, name: row.name, signalHash: row.signalHash })}
                              className="ml-1.5 inline-flex items-center gap-1 rounded-full border border-warning/50 bg-warning-light/40 px-2 py-0.5 text-xs font-semibold text-warning hover:bg-warning-light/70"
                            >
                              <ShieldAlert className="h-3 w-3" aria-hidden /> Correct here
                            </button>
                          );
                        }
                        return null;
                      })()}
                    </td>
                    <td className="px-3 py-2">
                      <select
                        value={value}
                        onChange={(e) => onEdit(row, e.target.value || null)}
                        className={cn(
                          'min-h-[44px] w-full rounded-md border bg-card/60 px-2 py-1.5 text-sm text-foreground',
                          edited ? 'border-primary/60' : 'border-border',
                        )}
                      >
                        {!row.role && <option value="">Needs your call…</option>}
                        {row.candidateRoles.map((cr) => (
                          <option key={cr.role} value={cr.role}>{cr.label}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </GlassPanel>
      )}
      {correcting && organizationId && (
        <CorrectionDialog
          contamination={correcting.contamination}
          accountName={correcting.name}
          signalHash={correcting.signalHash}
          organizationId={organizationId}
          onClose={() => setCorrecting(null)}
        />
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* The grouped resolution surface — groups entry, filterable sectioned table  */
/* -------------------------------------------------------------------------- */

/**
 * One name-suggested cluster: N unanswered accounts sharing a single Alfie
 * suggestion, accepted in one action (staged as approved-as-a-group; each row
 * stays individually editable in the table until — and after — Save).
 */
function SuggestionGroupRow({
  group,
  chart,
  onAccept,
}: {
  group: { role: string; label: string; cards: XeroUnlockCard[] };
  chart: XeroChartRow[];
  onAccept: () => void;
}) {
  const [open, setOpen] = useState(false);
  const nameById = new Map(chart.map((r) => [r.accountId, r.name]));
  return (
    <div className="mt-3 rounded-xl border border-border bg-card/40 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <AlfieRadialHub size={16} />
        <p className="min-w-0 flex-1 text-sm text-foreground">
          Alfie suggests <span className="font-medium">{group.label}</span> for{' '}
          {group.cards.length} accounts
        </p>
      </div>
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className="mt-1 inline-flex min-h-[36px] items-center gap-1 py-1 text-xs text-muted-foreground hover:text-foreground"
      >
        <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-180')} />
        {open ? 'Hide the accounts' : 'See the accounts'}
      </button>
      {open && (
        <ul className="mt-1 max-h-48 space-y-0.5 overflow-y-auto text-xs text-muted-foreground">
          {group.cards.map((c) => (
            <li key={c.accountId} className="truncate">
              <span className="text-muted-foreground/70">{c.code}</span>{' '}
              {nameById.get(c.accountId) ?? c.accountId}
            </li>
          ))}
        </ul>
      )}
      <Button size="sm" className="mt-2 min-h-[40px]" onClick={onAccept}>
        <Check className="mr-1.5 h-4 w-4" aria-hidden />
        Accept for all {group.cards.length}
      </Button>
    </div>
  );
}

/** Which resolution group a row sits in right now (staged edits overlaid). */
type RowStatus = 'staged' | 'needs' | 'auto' | 'sorted';

function rowStatusOf(row: XeroChartRow, answers: Record<string, Answer>): RowStatus {
  if (answers[row.accountId] !== undefined) return 'staged';
  if (row.role) return 'sorted';
  if (row.defaultRole) return 'auto';
  return 'needs';
}

/** Semantic status chip — icon + label, never colour alone (both themes). */
function RowStatusChip(
  { row, status, upgrade }: {
    row: XeroChartRow;
    status: RowStatus;
    upgrade?: AutoResolveUpgrade;
  },
) {
  if (status === 'staged') {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-primary">
        <CircleDashed className="h-3 w-3" aria-hidden /> Pending save
      </span>
    );
  }
  if (status === 'needs') {
    const conflict = row.consistency === 'conflict';
    return (
      <span className="inline-flex items-center gap-1 text-xs text-warning">
        {conflict
          ? <AlertTriangle className="h-3 w-3" aria-hidden />
          : <HelpCircle className="h-3 w-3" aria-hidden />}
        {conflict ? 'Mixed signals — needs your decision' : 'Needs your call'}
      </span>
    );
  }
  if (status === 'auto') {
    // Alfie's name/product-inferred role upgrades the generic catch-all default.
    if (upgrade) {
      return (
        <span className="inline-flex items-center gap-1 text-xs text-info">
          <HearthstoneIcon size={12} className="shrink-0" />
          Auto-resolve: {upgrade.roleLabel}
          <span className="text-muted-foreground">· AI-refined</span>
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 text-xs text-info">
        <HearthstoneIcon size={12} className="shrink-0" /> Auto-resolve: {row.defaultRoleLabel}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs text-success">
      <CheckCircle2 className="h-3 w-3" aria-hidden />
      {row.confirmedState === 'bulk_accept' ? `${row.roleLabel} · approved as a group` : row.roleLabel}
    </span>
  );
}

const STATUS_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'auto', label: 'Auto-resolve' },
  { id: 'needs', label: 'Needs you' },
  { id: 'sorted', label: 'Sorted' },
] as const;
type StatusFilter = (typeof STATUS_FILTERS)[number]['id'];

/** Fixed classification order for the sectioned table. */
const SECTION_ORDER = [
  'revenue',
  'other-income',
  'cost-of-sales',
  'operating-expense',
  'asset',
  'liability',
  'equity',
] as const;

/**
 * The at-a-glance table: every account, grouped by classification, role visible
 * per row. Desktop gets an inline band-locked select; mobile rows open a bottom
 * sheet (44px options, evidence line, Xero fix hint) — no horizontal scroll.
 */
function SectionedChart({
  chart,
  answers,
  onEdit,
  filter,
  shortCode,
  upgrades,
}: {
  chart: XeroChartRow[];
  answers: Record<string, Answer>;
  onEdit: (row: XeroChartRow, role: string | null) => void;
  filter: StatusFilter;
  shortCode: string | null;
  upgrades: Map<string, AutoResolveUpgrade>;
}) {
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
  const [sheetRowId, setSheetRowId] = useState<string | null>(null);

  const sections = useMemo(() => {
    const by = new Map<string, XeroChartRow[]>();
    for (const row of chart) {
      const key = row.classification ?? 'unrecognised';
      if (!by.has(key)) by.set(key, []);
      by.get(key)!.push(row);
    }
    const order = [...SECTION_ORDER.filter((s) => by.has(s)), ...[...by.keys()].filter(
      (k) => !(SECTION_ORDER as readonly string[]).includes(k),
    )];
    return order.map((key) => ({ key, rows: by.get(key)! }));
  }, [chart]);

  const visible = (row: XeroChartRow) => {
    if (filter === 'all') return true;
    const s = rowStatusOf(row, answers);
    // A staged row stays visible in the group it is being resolved FROM.
    if (s === 'staged') return filter !== 'sorted';
    return s === filter;
  };

  const sheetRow = sheetRowId ? chart.find((r) => r.accountId === sheetRowId) ?? null : null;
  const sheetAnswer = sheetRow ? answers[sheetRow.accountId] : undefined;
  const sheetValue = sheetAnswer !== undefined ? sheetAnswer.role : sheetRow?.role ?? null;

  return (
    <div className="mt-3 space-y-2">
      {sections.map(({ key, rows }) => {
        const shown = rows.filter(visible);
        if (!shown.length) return null;
        const attention = rows.filter((r) => {
          const s = rowStatusOf(r, answers);
          return s === 'needs' || s === 'auto';
        }).length;
        // Sections with open work start expanded; settled ones start collapsed.
        const open = openSections[key] ?? attention > 0;
        return (
          <GlassPanel key={key} intensity="light" rounded="2xl" className="overflow-hidden">
            <button
              type="button"
              onClick={() => setOpenSections((p) => ({ ...p, [key]: !open }))}
              className="flex min-h-[44px] w-full items-center gap-2 px-3 py-2 text-left"
            >
              <ChevronDown
                className={cn(
                  'h-4 w-4 shrink-0 text-muted-foreground transition-transform',
                  open && 'rotate-180',
                )}
                aria-hidden
              />
              <span className="text-sm font-medium capitalize text-foreground">
                {key.replace(/-/g, ' ')}
              </span>
              <span className="ml-auto shrink-0 text-xs text-muted-foreground">
                {shown.length} account{shown.length === 1 ? '' : 's'}
                {attention > 0 && <span className="text-warning"> · {attention} open</span>}
              </span>
            </button>
            {open && shown.map((row) => {
              const status = rowStatusOf(row, answers);
              const staged = answers[row.accountId];
              const value = staged !== undefined ? staged.role ?? '' : row.role ?? '';
              return (
                <div
                  key={row.accountId}
                  className="flex items-center gap-3 border-t border-border/60 px-3 py-2"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm text-foreground">
                      <span className="text-muted-foreground">{row.code}</span> {row.name}
                    </p>
                    <RowStatusChip row={row} status={status} upgrade={upgrades.get(row.accountId)} />
                  </div>
                  {/* Desktop: inline band-locked select. */}
                  <select
                    value={value}
                    onChange={(e) => onEdit(row, e.target.value || null)}
                    className={cn(
                      'hidden min-h-[36px] w-52 shrink-0 rounded-md border bg-card/60 px-2 py-1.5 text-sm text-foreground sm:block',
                      staged !== undefined ? 'border-primary/60' : 'border-border',
                    )}
                  >
                    {!row.role && <option value="">Choose a role…</option>}
                    {row.candidateRoles.map((cr) => (
                      <option key={cr.role} value={cr.role}>{cr.label}</option>
                    ))}
                  </select>
                  {/* Mobile: the row opens a bottom sheet. */}
                  <button
                    type="button"
                    onClick={() => setSheetRowId(row.accountId)}
                    className="min-h-[44px] shrink-0 rounded-full border border-border bg-card/60 px-3 py-1.5 text-xs text-foreground sm:hidden"
                  >
                    {staged !== undefined
                      ? (staged.role
                        ? row.candidateRoles.find((c) => c.role === staged.role)?.label ?? 'Chosen'
                        : 'Cleared')
                      : row.roleLabel ?? row.defaultRoleLabel ?? 'Choose'}
                  </button>
                </div>
              );
            })}
          </GlassPanel>
        );
      })}

      {/* Mobile role picker — bottom sheet, evidence first, 44px options. */}
      <Sheet open={!!sheetRow} onOpenChange={(o) => !o && setSheetRowId(null)}>
        <SheetContent
          side="bottom"
          className="max-h-[85dvh] overflow-y-auto rounded-t-2xl pb-[calc(1.25rem+env(safe-area-inset-bottom,0px))]"
        >
          {sheetRow && (
            <>
              <SheetHeader className="text-left">
                <SheetTitle className="text-base">
                  <span className="text-muted-foreground">{sheetRow.code}</span> {sheetRow.name}
                </SheetTitle>
              </SheetHeader>
              {sheetRow.reason && (
                <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                  {sheetRow.reason}
                </p>
              )}
              <div className="mt-3 space-y-2">
                {sheetRow.candidateRoles.map((cr) => {
                  const selected = sheetValue === cr.role;
                  return (
                    <button
                      key={cr.role}
                      type="button"
                      onClick={() => {
                        onEdit(sheetRow, cr.role);
                        setSheetRowId(null);
                      }}
                      className={cn(
                        'flex min-h-[44px] w-full items-center justify-between rounded-xl border px-4 py-2 text-left text-sm transition-colors',
                        selected
                          ? 'border-primary bg-primary/10 text-foreground'
                          : 'border-border bg-card/60 text-foreground',
                      )}
                    >
                      {cr.label}
                      {selected && <Check className="h-4 w-4 text-primary" aria-hidden />}
                    </button>
                  );
                })}
              </div>
              {sheetRow.consistency === 'conflict' && (
                <div className="mt-3 border-t border-border/60 pt-3">
                  <XeroFixHint kind="conflict" shortCode={shortCode} />
                </div>
              )}
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Completed state — the finance landing                                      */
/* -------------------------------------------------------------------------- */

// Xero P&L figures are MAJOR units (dollars), not the minor-unit cents the app's
// formatAud/formatCents helpers expect — so those don't apply here. Currency is
// hardcoded AUD: the snapshot doesn't yet carry the org's base currency, so a
// non-AU org would render the wrong symbol. Deferred — needs org-currency on the
// snapshot before this can be right (tracked with the reports plan).
function money(n: number | null): string {
  if (n === null) return '—';
  return n.toLocaleString('en-AU', {
    style: 'currency',
    currency: 'AUD',
    maximumFractionDigits: 0,
  });
}

/* -------------------------------------------------------------------------- */
/* The metric fixer — focus-the-fix flow for a blocked metric                  */
/* -------------------------------------------------------------------------- */

/**
 * A blocked metric names an ABSENCE (no account feeds a role), so the fix
 * can't be found by scanning the all-green chart. This row inverts the
 * direction: it offers the band-legal candidate accounts as chips (re-map,
 * staged through the normal save path) — and when the chart structurally
 * can't feed the metric (fewer legal accounts than missing roles), the
 * Xero-side split/create guide leads and the chips demote.
 */
function MetricFixerRow({
  metric,
  chart,
  effectiveChart,
  snapshotRoleTotals,
  shortCode,
  answers,
  onEdit,
  onUnstage,
  open,
  onToggle,
  autoFocus,
}: {
  metric: BlockedMetric;
  /** SAVED chart — checklist truth. */
  chart: XeroChartRow[];
  /** Chart with unsaved staged answers overlaid — all fixer maths use this. */
  effectiveChart: XeroChartRow[];
  /** Current month's role figures (advisor #6: mapped is not the same as fed). */
  snapshotRoleTotals: Record<string, number> | null | undefined;
  shortCode: string | null;
  answers: Record<string, Answer>;
  onEdit: (row: XeroChartRow, role: string | null) => void;
  onUnstage: (accountId: string) => void;
  open: boolean;
  onToggle: () => void;
  autoFocus: boolean;
}) {
  const ref = useRef<HTMLLIElement>(null);
  const [showSwaps, setShowSwaps] = useState(false);
  useEffect(() => {
    if (autoFocus) ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [autoFocus]);

  const savedHolders = new Set(chart.map((r) => r.role).filter(Boolean) as string[]);
  const effectiveHolders = new Set(effectiveChart.map((r) => r.role).filter(Boolean) as string[]);
  const savedById = new Map(chart.map((r) => [r.accountId, r]));

  // Roles still uncovered AFTER staging — only these need options.
  const stagedMissing = metric.missingRoles.filter((r) => !effectiveHolders.has(r));
  const plan = metricFixPlan(stagedMissing, effectiveChart);
  const unwinnable = stagedMissing.length > 0 && !plan.canUnlockByMapping;
  // Staged-but-unsaved picks for this metric's roles — undoable until Save.
  const stagedPicks = metric.missingRoles
    .filter((r) => !savedHolders.has(r) && effectiveHolders.has(r))
    .map((r) => ({
      role: r,
      row: effectiveChart.find((c) => c.role === r && answers[c.accountId]?.role === r) ?? null,
    }));

  const stage = (row: XeroChartRow, role: string) => {
    const prior = answers[row.accountId];
    if (prior !== undefined && prior.role !== role) {
      toast.info(`${row.name} was staged elsewhere — moved here instead.`);
    }
    onEdit(row, role);
  };

  /** Checklist state per required role (advisor #6/#11/#13). */
  const checklist = metric.requiredRoles.map((role) => {
    const covered = savedHolders.has(role);
    const pending = !covered && effectiveHolders.has(role);
    const noFigures = covered && snapshotRoleTotals != null && snapshotRoleTotals[role] == null;
    return { role, label: roleLabelShort(role), covered, pending, noFigures };
  });

  const chip = (row: XeroChartRow, role: string, consequence: string | null) => (
    <button
      key={row.accountId}
      type="button"
      onClick={() => stage(row, role)}
      className="min-h-[44px] rounded-full border border-border bg-card/60 px-4 py-2 text-left text-sm text-foreground transition-colors hover:border-primary/40"
    >
      {row.code ? `${row.code} · ` : ''}{row.name}
      <span className="block text-xs text-muted-foreground">
        currently {savedById.get(row.accountId)?.roleLabel ?? 'unassigned'}
        {consequence && <span className="text-warning"> — {consequence}</span>}
      </span>
    </button>
  );

  const freeSections = plan.perRole.filter((g) => g.free.length > 0);
  const swapSections = plan.perRole.filter((g) => g.swap.length > 0);

  return (
    <li ref={ref} className="text-sm">
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden />
        <div className="min-w-0 flex-1">
          <span className="font-medium text-foreground">{metric.label}</span>
          {/* The checklist — positions for every feed, so a correction visibly
              lands even when another feed is still missing. */}
          <span className="mt-0.5 flex flex-wrap gap-x-2.5 gap-y-0.5 text-[13px] leading-[18px]">
            {checklist.map((c) => (
              <span
                key={c.role}
                className={cn(
                  c.pending
                    ? 'text-primary'
                    : !c.covered
                    ? 'text-warning'
                    : c.noFigures
                    ? 'text-warning'
                    : 'text-success',
                )}
                title={c.noFigures
                  ? 'Mapped, but no transactions hit it this month — recode them onto it in Xero.'
                  : undefined}
              >
                {c.label}{' '}
                {c.pending ? '· pending save' : !c.covered ? '✗' : c.noFigures ? '✓ no figures yet' : '✓'}
              </span>
            ))}
          </span>
        </div>
        <button
          type="button"
          onClick={onToggle}
          className="min-h-[36px] shrink-0 py-1 text-xs font-semibold text-primary"
        >
          {open ? 'Close' : 'Fix this →'}
        </button>
      </div>

      {open && (
        <div className="mt-2 space-y-4 rounded-xl border border-border bg-card/40 p-3 md:ml-6">
          {/* Undo line for staged picks — the question collapses once staged,
              so the way back must stay visible until Save. */}
          {stagedPicks.map((p) =>
            p.row
              ? (
                <p key={p.role} className="text-xs text-muted-foreground">
                  <span className="text-primary">{roleLabelShort(p.role)}</span> → {p.row.name}{' '}
                  (pending save ·{' '}
                  <button
                    type="button"
                    onClick={() => onUnstage(p.row!.accountId)}
                    className="text-primary hover:underline"
                  >
                    undo
                  </button>
                  )
                </p>
              )
              : null
          )}

          {unwinnable && (
            /* Re-assignment mathematically cannot win here — say the WHY,
               per role (advisor #7: the explanation derives from the same
               flow as the verdict, so they can never contradict), and lead
               with the only real exit. Chips that can't succeed are noise,
               so swaps drop behind a toggle below. */
            <>
              <div className="space-y-1 text-[13px] leading-snug text-foreground">
                {plan.unfilled.map((u) => (
                  <p key={u.role}>
                    {u.plausibleCount === 0
                      ? (
                        <>
                          Nothing in your chart could be your{' '}
                          <span className="font-medium">{roleLabel(u.role)}</span> — it doesn&apos;t
                          exist as its own account yet.
                        </>
                      )
                      : (
                        <>
                          No account that could be your{' '}
                          <span className="font-medium">{roleLabel(u.role)}</span> is free to move —
                          the {u.plausibleCount === 1 ? 'one' : u.plausibleCount} that could{' '}
                          {u.plausibleCount === 1 ? 'is' : 'are'} already doing other jobs.
                        </>
                      )}
                  </p>
                ))}
                {swapSections.length > 0 && (
                  <p className="text-muted-foreground">
                    Re-assigning them can&apos;t unlock this — it only moves the gap.
                  </p>
                )}
              </div>
              <XeroFixHint kind="create" shortCode={shortCode} />
            </>
          )}

          {/* Genuinely safe moves — always first-class. Each question is
              signposted with an amber serif title and a hairline divider from
              the one above, so consecutive questions don't blur together. */}
          {freeSections.map((g) => (
            <div key={g.role} className="border-t border-border/60 pt-4 first:border-t-0 first:pt-0">
              <p className="text-[15px] font-semibold leading-5 text-warning">
                Which account holds your {roleLabel(g.role)}?
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {g.free.map((row) => chip(row, g.role, null))}
              </div>
            </div>
          ))}

          {/* Swaps: first-class (with consequence) when mapping can win;
              behind an explicit toggle when it can't — every one is labelled
              with what it does NOT achieve. */}
          {swapSections.length > 0 && (unwinnable
            ? (
              <div>
                <button
                  type="button"
                  onClick={() => setShowSwaps((s) => !s)}
                  className="min-h-[36px] py-1 text-xs text-muted-foreground hover:text-foreground"
                >
                  {showSwaps ? 'Hide' : 'Think one of these is mis-assigned? Show them'}
                </button>
                {showSwaps && swapSections.map((g) => (
                  <div key={g.role} className="mt-1">
                    <p className="text-xs text-muted-foreground">
                      If an account below is truly your {roleLabel(g.role)}, move it — but this
                      won&apos;t unlock {metric.label}, it only moves the gap:
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {g.swap.map((s) =>
                        chip(s.row, g.role, `leaves ${roleLabelShort(s.vacates)} empty`)
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )
            : swapSections.map((g) => (
              <div key={g.role} className="border-t border-border/60 pt-4 first:border-t-0 first:pt-0">
                <p className="text-[15px] font-semibold leading-5 text-warning">
                  Or, if one account already holds your {roleLabel(g.role)}:
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {g.swap.map((s) =>
                    chip(
                      s.row,
                      g.role,
                      `moving it leaves ${roleLabelShort(s.vacates)} empty; split it in Xero if it's really both`,
                    )
                  )}
                </div>
              </div>
            )))}

          {/* Conflict-flagged band-mates never become chips — their type is the
              thing in doubt (advisor #7/#8). */}
          {plan.checkFirst.length > 0 && (
            <div className="border-t border-border/60 pt-3">
              <p className="mb-2 text-xs text-warning">
                Alfie can&apos;t trust {plan.checkFirst.length === 1 ? 'this account' : 'these accounts'} yet:{' '}
                {plan.checkFirst.map((r) => r.name).join(', ')} — check the account type in Xero first.
              </p>
              <XeroFixHint kind="conflict" shortCode={shortCode} />
            </div>
          )}

          {stagedMissing.length > 0 && !unwinnable && (
            <div className="border-t border-border/60 pt-3">
              <p className="mb-2 text-xs text-muted-foreground">
                None of these? It may not exist as its own account in Xero yet.
              </p>
              <XeroFixHint kind="create" shortCode={shortCode} />
            </div>
          )}
        </div>
      )}
    </li>
  );
}

function CompletedDashboard({
  tenantName,
  snapshot,
  snapshotLoading,
  chart,
  coverage,
  chartDrift,
  health,
  healthInputs,
  healthAcks,
  healthDeclarations,
  cashDesignations,
  aliases,
  unplacedDispositions,
  missedCodes,
  organizationId,
  shortCode,
  focusFix,
  onConsumeFix,
  focusAccount,
  focusAudit = false,
  onConsumeFixAccount,
  answers,
  onEdit,
  onUnstage,
  onRefresh,
  syncing,
  onDisconnect,
  disconnecting,
  canDisconnect,
}: {
  tenantName: string | null;
  snapshot: XeroSnapshot | null;
  snapshotLoading: boolean;
  chart: XeroChartRow[];
  /** Role coverage from classify — drives the metrics-ready/blocked panel. */
  coverage: { present: string[]; missing: string[] };
  /** Structural drift from the sweep — highlights the refresh cluster. */
  chartDrift: XeroConnection['chartDrift'];
  /** The stored 40-rule health report (F-013 S2). */
  health: XeroConnection['health'];
  /** The connection's health_inputs jsonb — deep-link handles for the fix buttons. */
  healthInputs: unknown;
  /** The connection's health_acks jsonb — org-level "got it" state. */
  healthAcks: unknown;
  /** The connection's health_declarations jsonb — "About your books" answers. */
  healthDeclarations: unknown;
  /** The operating-accounts picker's stored state (from classify). */
  cashDesignations: Record<string, XeroCashDesignation>;
  aliases: Array<{ oldName: string; accountId: string }>;
  unplacedDispositions: Array<{ code: string; disposition: string }>;
  /** Flow guards' unresolved codes (from the connection) — the unplaced list. */
  missedCodes: Array<{ code: string; count: number; volume: number; net: number }>;
  /** For the supplier-ABN review dialog (N-3). */
  organizationId: string;
  /** Deep-link key for "Open in Xero" (lib/xero-links). */
  shortCode: string | null;
  /** Metric id from ?fix= — open that fixer and scroll to it on arrival. */
  focusFix: string | null;
  /** Strip the ?fix= param once applied (advisor #10 — it must not re-fire). */
  onConsumeFix: () => void;
  /** Account guid from ?fixAccount= — expand+scroll the classified row (+ open the correction dialog). */
  focusAccount: string | null;
  /** Strip ?fixAccount= once applied (independent of ?fix). */
  onConsumeFixAccount: () => void;
  /** ?audit=1 — open the health audit and bring it into view (the cockpit's "findings to review"). */
  focusAudit?: boolean;
  answers: Record<string, Answer>;
  onEdit: (row: XeroChartRow, role: string | null) => void;
  onUnstage: (accountId: string) => void;
  onRefresh: () => void;
  syncing: boolean;
  onDisconnect: (deleteMappings: boolean) => void;
  disconnecting: boolean;
  canDisconnect: boolean;
}) {
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false);
  // One fixer open at a time; ?fix= opens its metric's fixer on arrival, then
  // is consumed — otherwise every save/refetch re-opens and re-scrolls it.
  const [openFix, setOpenFix] = useState<string | null>(focusFix);
  useEffect(() => {
    if (focusFix) {
      setOpenFix(focusFix);
      onConsumeFix();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusFix]);

  // ?fixAccount= — hold the target account in state (survives the param strip, like openFix) and
  // hand it to ResolvedChart, which expands + scrolls + opens the correction dialog. Consumed on
  // arrival so a save/refetch never re-fires it.
  const [openAccount, setOpenAccount] = useState<string | null>(focusAccount);
  useEffect(() => {
    if (focusAccount) {
      setOpenAccount(focusAccount);
      onConsumeFixAccount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusAccount]);

  // Stable (memoised) so ResolvedChart's ?fixAccount effect fires once the snapshot hydrates,
  // even if that lands a tick after arrival (a fresh Map every render would re-run every render).
  const contaminationsByAccount = useMemo(
    () => new Map((snapshot?.contaminations ?? []).map((c) => [c.accountId, c])),
    [snapshot],
  );
  const correctedAccountIds = useMemo(
    () => new Set((snapshot?.corrections ?? []).map((c) => c.accountId)),
    [snapshot],
  );

  // Unsaved staged answers overlaid — every piece of fixer maths reads this,
  // never the saved chart alone (advisor #4).
  const effectiveChart = useMemo(() => applyStagedAnswers(chart, answers), [chart, answers]);

  const banks = chart.filter((r) => r.role === 'cash-bucket' || r.role === 'tax-reserve-cash');
  const banksToConfirm = banks.filter((b) => !cashDesignations[b.accountId]).length;
  // ONE large section open at a time (Tim, 2026-08-31): unplaced / audit / classified
  // list start collapsed with prominent count flags; the last-opened one is remembered
  // per device once the builder starts interacting.
  const [openSection, setOpenSectionState] = useState<'unplaced' | 'audit' | 'list' | null>(() => {
    try {
      const v = localStorage.getItem('fb-xero-landing-open');
      return v === 'unplaced' || v === 'audit' || v === 'list' ? v : null;
    } catch {
      return null;
    }
  });
  const setOpenSection = (v: 'unplaced' | 'audit' | 'list' | null) => {
    setOpenSectionState(v);
    try {
      if (v) localStorage.setItem('fb-xero-landing-open', v);
      else localStorage.removeItem('fb-xero-landing-open');
    } catch { /* per-device convenience only */ }
  };
  // Coverage line expands only for real work; a ?fix= arrival forces it open.
  const [coverageOpen, setCoverageOpen] = useState<boolean>(() => Boolean(focusFix));
  useEffect(() => {
    if (focusFix) setCoverageOpen(true);
  }, [focusFix]);
  const pickerRef = useRef<HTMLDivElement>(null);
  const unplacedRef = useRef<HTMLDivElement>(null);
  const auditRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const scrollToSection = (ref: React.RefObject<HTMLDivElement>) => {
    // Let the section open/expand first, then bring its top into view.
    setTimeout(() => ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
  };
  // ?audit=1 lands on the OPEN audit, scrolled into view (Tim, 13 Oct 2026: the cockpit's
  // "12 findings to review" used to open the ledger, where the findings aren't).
  useEffect(() => {
    if (!focusAudit) return;
    setOpenSection('audit');
    scrollToSection(auditRef);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusAudit]);
  // A ?fixAccount= deep link must land on the OPEN list.
  useEffect(() => {
    if (openAccount) setOpenSection('list');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openAccount]);

  // The picker's STAGED entrance (Tim, 2026-08-31): the page loads with the quiet
  // account list in the rail, waits a beat, ANIMATES OUT to full width, and only then
  // the decision UI fades in. Auto-opens ONCE per visit; after Confirm it tucks away
  // and never re-opens itself (the Change button is the way back in).
  const [pickerEditing, setPickerEditing] = useState(false);
  const pickerAutoOpened = useRef(false);
  const needsBankConfirm = banks.length > 1 && banks.some((b) => !cashDesignations[b.accountId]);
  useEffect(() => {
    if (!needsBankConfirm || pickerAutoOpened.current) return;
    pickerAutoOpened.current = true;
    const t = setTimeout(() => setPickerEditing(true), 700);
    return () => clearTimeout(t);
  }, [needsBankConfirm]);
  // Same requirements table the cockpit computes from — the wizard can never
  // say "all verified" while the dashboard shows a metric grey (they agree by
  // construction).
  const blocked = blockedMetrics(coverage.missing as AccountRole[]);
  const ready = readyMetrics(coverage.missing as AccountRole[]);
  // The page's one-line story: allocation state first, then the audit's verdict.
  const auditPart = health
    ? partitionHealth((health as XeroHealthReport).results, parseHealthAcks(healthAcks))
    : null;
  // Unplaced transactions still needing an answer (same filter the card applies).
  const unplacedDone = new Set([
    ...unplacedDispositions.map((d) => d.code),
    ...aliases.map((a) => a.oldName),
  ]);
  const unplacedCount = missedCodes.filter((m) => !unplacedDone.has(m.code)).length;
  // "About your books" — answered in the standard question-flow SCREEN (swipe
  // template), not inline; this landing only summarises + launches it.
  const booksAnswers = parseHealthDeclarations(healthDeclarations);
  const [booksFlow, setBooksFlow] = useState(false);
  const declareMutation = useDeclareXeroHealth(organizationId || null);
  // Clickable narrator (Tim): each segment opens + scrolls to its section.
  const narratorParts = auditPart
    ? ([
      {
        text: `${chart.length} accounts sorted`,
        onClick: () => {
          setOpenSection('list');
          scrollToSection(listRef);
        },
      },
      banks.length > 1 && banksToConfirm > 0
        ? {
          text: `${banksToConfirm} bank account${banksToConfirm === 1 ? '' : 's'} to confirm`,
          onClick: () => {
            setPickerEditing(true);
            scrollToSection(pickerRef);
          },
        }
        : null,
      unplacedCount > 0
        ? {
          text: `${unplacedCount} transaction group${unplacedCount === 1 ? '' : 's'} to place`,
          onClick: () => {
            setOpenSection('unplaced');
            scrollToSection(unplacedRef);
          },
        }
        : null,
      auditPart.findings.length === 0 ? { text: 'audit clear' } : {
        text: `${auditPart.findings.length} audit finding${
          auditPart.findings.length === 1 ? '' : 's'
        }`,
        onClick: () => {
          setOpenSection('audit');
          scrollToSection(auditRef);
        },
      },
    ].filter(Boolean) as Array<{ text: string; onClick?: () => void }>)
    : null;
  const pnl: Array<[string, number | null, boolean]> = snapshot
    ? [
      ['Revenue', snapshot.revenue, false],
      ['Cost of sales', snapshot.costOfSales, false],
      ['Gross profit', snapshot.grossProfit, true],
      ['Operating expenses', snapshot.operatingExpenses, false],
      ['Net profit', snapshot.netProfit, true],
    ]
    : [];
  const tie = snapshot?.tieOut ?? null;

  // Mobile-first: the DOM is authored in mobile PRIORITY order (KPI button →
  // banks → org → tie-out → P&L → classified → disconnect) so on a single
  // column it stacks most-relevant-first with no horizontal overflow. Desktop
  // rebuilds the two-column layout via explicit `md:` row/col placement on every
  // block — a 2D rearrangement `order-*` can't express — so desktop is unchanged.
  // "About your books" answering is its OWN SCREEN (the app's question-response
  // template): the landing swaps out entirely, and Save/Cancel bring it back.
  if (booksFlow) {
    return (
      <BooksQuestionFlow
        initial={booksAnswers}
        saving={declareMutation.isPending}
        error={declareMutation.error instanceof Error ? declareMutation.error.message : null}
        onSave={(answers) => {
          declareMutation.mutate(answers as Parameters<typeof declareMutation.mutate>[0], {
            onSuccess: () => setBooksFlow(false),
          });
        }}
        onCancel={() => setBooksFlow(false)}
      />
    );
  }

  return (
    <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-[240px_1fr]">
      {/* KPI Dashboard + Refresh — top on mobile, full-width row 2 on desktop */}
      <div className="flex flex-wrap items-center justify-between gap-3 md:col-span-2 md:row-start-2">
        <Link
          to="/finance"
          className="inline-flex min-h-[48px] items-center gap-2 rounded-2xl bg-primary px-5 text-primary-foreground shadow-sm transition-colors hover:bg-primary/90"
        >
          <BarChart3 className="h-5 w-5" aria-hidden />
          <span className="font-medium">KPI Dashboard</span>
          <ArrowRight className="h-4 w-4" aria-hidden />
        </Link>
        {/* One line narrates the page — every segment is a door (opens + scrolls). */}
        {narratorParts && (
          <span className="flex flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground">
            {narratorParts.map((p, i) => (
              <span key={p.text} className="flex items-center gap-x-1.5">
                {i > 0 && <span aria-hidden>·</span>}
                {p.onClick
                  ? (
                    <button
                      type="button"
                      onClick={p.onClick}
                      className="min-h-[32px] font-medium text-primary hover:underline"
                    >
                      {p.text}
                    </button>
                  )
                  : <span>{p.text}</span>}
              </span>
            ))}
          </span>
        )}
        {/* Cadence + ⓘ + refresh; the fix lives on THIS page, so no drift link. */}
        <SyncStatusCluster
          fetchedAt={snapshot?.fetchedAt ?? null}
          chartDrift={chartDrift}
          tieOutValid={snapshot?.tieOut?.valid ?? null}
          onRefresh={onRefresh}
          syncing={syncing}
          showDriftLink={false}
        />
      </div>


      {/* Org context — below accounts on mobile, full-width row 1 on desktop */}
      <GlassPanel
        intensity="medium"
        rounded="2xl"
        className="flex flex-wrap items-center gap-3 px-4 py-3 md:col-span-2 md:row-start-1"
      >
        <div className="flex min-w-0 items-center gap-2.5">
          <Building2 className="h-5 w-5 shrink-0 text-primary" aria-hidden />
          <div className="min-w-0">
            <p className="ds-h3 truncate">
              {tenantName ?? 'Your Xero'}
            </p>
            <p className="text-xs text-muted-foreground">
              Connected{snapshot?.asAt ? ` · figures ${figuresCoverLabel(snapshot.asAt, snapshot.fetchedAt)}` : ''}
            </p>
          </div>
        </div>
      </GlassPanel>

      {/* Tie-out confirmation — full-width row 4 on desktop. The hint lives
          INSIDE this block so the explicit md: grid placement is untouched. */}
      {tie && (
        <div className="px-1 md:col-span-2 md:row-start-4">
          <div
            className={cn(
              'flex items-start gap-2 text-xs',
              tie.valid ? 'text-success' : 'text-warning',
            )}
          >
            {tie.valid
              ? <ShieldCheck className="h-4 w-4 shrink-0" />
              : <AlertTriangle className="h-4 w-4 shrink-0" />}
            <span className="min-w-0">
              {tie.valid
                ? `Tie-out confirmed — your figures match Xero's own totals to the cent.${
                  tie.unmapped > 0
                    ? ` (${tie.unmapped} account${tie.unmapped === 1 ? '' : 's'} outside the reconciled groups.)`
                    : ''
                }`
                : `Tie-out needs attention — the figures don't fully match Xero's totals${
                  tie.unmappedAccounts?.length
                    ? ` (unmapped: ${tie.unmappedAccounts.slice(0, 3).map((a) => a.name).join(', ')}${
                      tie.unmapped > 3 ? ` and ${tie.unmapped - 3} more` : ''
                    } — correct them in the list below)`
                    : tie.unmapped > 0
                    ? ` (${tie.unmapped} account${tie.unmapped === 1 ? '' : 's'} unmapped)`
                    : ''
                }.`}
            </span>
          </div>
          {!tie.valid && (
            <div className="mt-2">
              <XeroFixHint kind="unmapped" shortCode={shortCode} />
            </div>
          )}
        </div>
      )}

      {/* "About your books" — the five facts the audit can't pull from any API.
          Full-width row under the tie-out; collapses to a quiet line once all
          five are answered. */}
      <div className="md:col-span-2 md:row-start-5">
        <BooksDeclarations
          declarations={healthDeclarations}
          onAnswer={() => setBooksFlow(true)}
        />
      </div>

      {/* Quick P&L — right column row 3; slides aside while the picker borrows its
          space, and glides back (natural size, untouched) after Confirm. */}
      <AnimatePresence initial={false}>
      {!pickerEditing && (
      <motion.div
        key="quick-pnl"
        initial={{ opacity: 0, x: 16 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: 16 }}
        transition={{ duration: 0.22, ease: 'easeOut' }}
        className="md:col-start-2 md:row-start-3"
      >
      <GlassPanel
        intensity="medium"
        rounded="2xl"
        className="p-4"
      >
        <h3 className="ds-h3">This month</h3>
        {snapshot
          ? (
            <div className="mt-2 divide-y divide-border/60">
              {pnl.map(([label, val, strong]) => (
                <div key={label} className="flex items-baseline justify-between py-2">
                  <span
                    className={cn(
                      'text-sm',
                      strong ? 'font-medium text-foreground' : 'text-muted-foreground',
                    )}
                  >
                    {label}
                  </span>
                  <span
                    className={cn(
                      'tabular-nums',
                      strong ? 'font-semibold text-foreground' : 'text-foreground',
                      val !== null && val < 0 && 'text-danger',
                    )}
                  >
                    {money(val)}
                  </span>
                </div>
              ))}
            </div>
          )
          : snapshotLoading
          ? <p className="mt-2 text-sm text-muted-foreground">Loading your figures…</p>
          : (
            <p className="mt-2 text-sm text-muted-foreground">
              No figures yet — choose Refresh to pull them from Xero.
            </p>
          )}
      </GlassPanel>
      </motion.div>
      )}
      </AnimatePresence>

      {/* Bank accounts — the operating-accounts picker (what each account is FOR).
          Quiet = the left rail; editing = animates across the full row for pill room.
          DOM position (after the P&L) is the MOBILE order — "This month" first, then
          banks (Tim, 2026-09-02); desktop is untouched (explicit md: row placement). */}
      <motion.div
        ref={pickerRef}
        layout
        transition={{ type: 'spring', stiffness: 230, damping: 30 }}
        className={cn('md:row-start-3 scroll-mt-20', pickerEditing ? 'md:col-span-2' : 'md:col-start-1')}
      >
        <BankAccountsPicker
          organizationId={organizationId}
          accounts={banks.map((b) => ({ accountId: b.accountId, name: b.name, suggested: b.suggestedDesignation ?? null }))}
          stored={cashDesignations}
          monthlyOverhead={snapshot?.roleTotals?.['overhead'] ?? null}
          editing={pickerEditing}
          onEditingChange={setPickerEditing}
          unplacedCount={unplacedCount}
          onReviewUnplaced={() => {
            setOpenSection('unplaced');
            scrollToSection(unplacedRef);
          }}
        />
      </motion.div>

      {/* Activity Alfie can't place — unresolved codes with ACTIONS (alias / not-ours /
          dismiss). Renders nothing when everything is placed or answered. */}
      {missedCodes.length > 0 && (
        <div ref={unplacedRef} className="scroll-mt-20 md:col-span-2 md:row-start-6">
          <UnplacedActivity
            organizationId={organizationId}
            missedCodes={missedCodes}
            dispositions={unplacedDispositions}
            aliases={aliases}
            accounts={banks.map((b) => ({ accountId: b.accountId, name: b.name, suggested: b.suggestedDesignation ?? null }))}
            open={openSection === 'unplaced'}
            onOpenChange={(o) => setOpenSection(o ? 'unplaced' : null)}
          />
        </div>
      )}

      {/* Metrics coverage — the same truth the cockpit's data-health uses.
          Full-width row 6. Never claims "all verified" while a metric is grey. */}
      <GlassPanel intensity="medium" rounded="2xl" className="p-4 md:col-span-2 md:row-start-7">
        {/* Condensed (Tim, 2026-08-31): one quiet line, no advertising. It only grows when
            there's real work — blocked metrics with their fixers — or a ?fix= arrival. */}
        <button
          type="button"
          onClick={() => setCoverageOpen((o) => !o)}
          aria-expanded={coverageOpen}
          disabled={blocked.length === 0 && !focusFix}
          className="flex min-h-[36px] w-full items-center gap-2 text-left text-sm"
        >
          <CheckCircle2 className="h-4 w-4 shrink-0 text-success" aria-hidden />
          <span className="text-foreground">
            {ready.length} metric{ready.length === 1 ? '' : 's'} unlocked
          </span>
          {blocked.length > 0 && (
            <span className="rounded-full bg-warning-light/60 px-2.5 py-0.5 text-xs font-semibold text-warning">
              {blocked.length} waiting on your books
            </span>
          )}
          {(blocked.length > 0 || focusFix) && (
            <ChevronDown
              className={cn(
                'ml-auto h-4 w-4 shrink-0 text-muted-foreground transition-transform',
                coverageOpen && 'rotate-180',
              )}
              aria-hidden
            />
          )}
        </button>
        {coverageOpen && (
          <p className="mt-1.5 text-xs text-muted-foreground">
            Unlocked: {ready.map((r) => r.label).join(' · ')}
          </p>
        )}
        {/* Arrived via ?fix= for a metric that ISN'T blocked → say so plainly
            instead of a silent no-op (advisor #10). */}
        {coverageOpen && focusFix && !blocked.some((b) => b.id === focusFix) && (
          <p className="mt-2 flex items-center gap-1.5 text-sm text-success">
            <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden />
            {METRIC_REQUIREMENTS.find((m) => m.id === focusFix)?.label ?? 'That metric'} is ready —
            nothing to fix here.
          </p>
        )}
        {coverageOpen && blocked.length > 0 && (
          <ul className="mt-2 space-y-2.5">
            {blocked.map((b) => (
              <MetricFixerRow
                key={b.id}
                metric={b}
                chart={chart}
                effectiveChart={effectiveChart}
                snapshotRoleTotals={snapshot?.roleTotals}
                shortCode={shortCode}
                answers={answers}
                onEdit={onEdit}
                onUnstage={onUnstage}
                open={openFix === b.id}
                onToggle={() => setOpenFix(openFix === b.id ? null : b.id)}
                autoFocus={focusFix === b.id}
              />
            ))}
          </ul>
        )}
      </GlassPanel>

      {/* Classified accounts — collapsed, with a tick. Before the audit. */}
      <div ref={listRef} className="scroll-mt-20 md:col-span-2 md:row-start-8">
        <ResolvedChart
          open={openSection === 'list'}
          onOpenChange={(o) => setOpenSection(o ? 'list' : null)}
          chart={chart}
          answers={answers}
          onEdit={onEdit}
          done
          contaminationsByAccount={contaminationsByAccount}
          correctedAccountIds={correctedAccountIds}
          organizationId={organizationId}
          focusAccount={openAccount}
        />
      </div>

      {/* The audit — the page's CLOSING verdict (Tim, 2026-08-30): allocation
          work reads first, the audit reports on the finished allocation. Setup
          rules render with the allocation surfaces, not here. */}
      {health && (
        <div ref={auditRef} className="scroll-mt-20 md:col-span-2 md:row-start-9">
          <XeroHealthPanel
            open={openSection === 'audit'}
            onOpenChange={(o) => setOpenSection(o ? 'audit' : null)}
            health={health as XeroHealthReport}
            organizationId={organizationId}
            shortCode={shortCode}
            healthInputs={healthInputs}
            healthAcks={healthAcks}
            onMappingPage
          />
        </div>
      )}


      {/* Disconnect — full-width row 7. Two-step, and the second step asks the
          mapping question: keep the saved setup (default — a reconnect finds it
          again) or delete it with the connection. */}
      {canDisconnect && (
        <div className="pt-2 md:col-span-2 md:row-start-10">
          {confirmingDisconnect
            ? (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  Disconnect Xero? Alfie will stop reading your numbers until you reconnect. Your
                  saved account setup can be kept — a reconnect finds it again — or deleted along
                  with the connection.
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <Button
                    variant="destructive"
                    size="sm"
                    className="min-h-[44px] sm:min-h-9"
                    onClick={() => onDisconnect(false)}
                    disabled={disconnecting}
                  >
                    {disconnecting ? 'Disconnecting…' : 'Disconnect, keep my setup'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="min-h-[44px] text-danger hover:text-danger sm:min-h-9"
                    onClick={() => onDisconnect(true)}
                    disabled={disconnecting}
                  >
                    Disconnect and delete my setup
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="min-h-[44px] sm:min-h-9"
                    onClick={() => setConfirmingDisconnect(false)}
                    disabled={disconnecting}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )
            : (
              <Button
                variant="outline"
                onClick={() => setConfirmingDisconnect(true)}
                disabled={disconnecting}
                className="min-h-[44px] text-danger hover:text-danger"
              >
                <Unlink className="mr-2 h-4 w-4" />
                {disconnecting ? 'Disconnecting…' : 'Disconnect Xero'}
              </Button>
            )}
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Page                                                                       */
/* -------------------------------------------------------------------------- */

function XeroMappingPage() {
  const { fix, fixAccount, audit } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { data: orgData } = useCurrentOrganization();
  const organizationId = orgData?.organization?.id ?? null;

  const { stage, data, cards, upgrades, refreshing, refetch } = useXeroMappingFlow(organizationId);
  // A Xero screen: the normal Alfie answers in finance mode here (Tim, 31 Oct 2026). No topics of
  // its own, so questions go with the figures only.
  useEffect(() => (organizationId ? registerFinanceScreen('xero-mapping-page', organizationId) : undefined), [organizationId]);
  const confirm = useConfirmXeroMappings(organizationId);
  // The same resolution the ledger uses, so both surfaces attribute a decision identically.
  const accountingProposals = useMemo(
    () => (data ? resolveProposals(data.chart, cards, upgrades) : {}),
    [data, cards, upgrades],
  );
  const rowById = useMemo(
    () => new Map((data?.chart ?? []).map((r) => [r.accountId, r])),
    [data?.chart],
  );
  /**
   * The three settle fields, in ONE place, because this file has four save paths and they must
   * not disagree about what "against the signals" means. `proposed` is the role that was on
   * screen — `accountingProposals`, the same resolution the ledger uses — never the type's
   * catch-all: a builder taking Alfie's refinement has agreed, not overridden.
   */
  const cardById = useMemo(() => new Map(cards.map((c) => [c.accountId, c])), [cards]);
  const settleFields = useCallback(
    (accountId: string, role: string | null, proposed?: string | null) => {
      const r = rowById.get(accountId);
      return {
        decidedAgainstSignals: decidedAgainstSignals(role, {
          proposed: proposed !== undefined
            ? proposed
            : (accountingProposals[accountId]?.role ?? null),
          // BOTH sources, because they disagree. A card can be flagged `conflict` while its chart
          // row still reads `thin` — and `resolveProposals` SKIPS a conflict card, falling through
          // to the type's catch-all. Reading the row alone then records "picked the catch-all on a
          // conflict card" as agreement, which is the one thing this column must never do.
          conflict: r?.consistency === 'conflict' || cardById.get(accountId)?.flag === 'conflict',
        }),
        decidedXeroType: r?.xeroType ?? null,
        decidedReportCode: r?.reportingCode ?? null,
      };
    },
    [rowById, accountingProposals, cardById],
  );

  const status = useXeroStatus(organizationId);
  const sync = useSyncXeroProfitAndLoss(organizationId);
  const disconnect = useDisconnectXero(organizationId);
  const { isAdminOrOwner } = useOrgRole();

  const [answers, setAnswers] = useState<Record<string, Answer>>({});
  const [index, setIndex] = useState(0);
  const [dir, setDir] = useState(1);
  // The working surface: grouped entry (default) -> question flow or the table.
  const [view, setView] = useState<'groups' | 'cards' | 'table'>('groups');
  const [tableFilter, setTableFilter] = useState<StatusFilter>('all');
  // The explicit group approval — auto-resolution never applies without it.
  const [autoConsent, setAutoConsent] = useState(false);

  // Deep-link key for "Open in Xero" (null until the first sync/classify).
  const activeShortCode = status.data?.connections.find((c) => c.isActive)?.shortCode ?? null;

  const answeredCount = cards.filter((c) => answers[c.accountId] !== undefined).length;
  const total = cards.length;
  const currentCard = index < cards.length ? cards[index] : null;

  // Unresolved rows a type-default can settle — the "auto-resolve" group.
  const autoRows = useMemo(
    () => (data?.chart ?? []).filter((r) => !r.role && r.defaultRole),
    [data?.chart],
  );
  // The role we'll actually persist for an auto-resolve row: Alfie's AI-inferred
  // upgrade where it exists (e.g. "Materials (COGS)"), else the generic catch-all
  // default. `refined` distinguishes the two for display.
  const proposedFor = (r: XeroChartRow): { role: string; label: string; refined: boolean } => {
    const up = upgrades.get(r.accountId);
    return up
      ? { role: up.role, label: up.roleLabel, refined: true }
      : { role: r.defaultRole!, label: r.defaultRoleLabel ?? r.defaultRole!, refined: false };
  };
  const refinedCount = useMemo(
    () => autoRows.filter((r) => upgrades.has(r.accountId)).length,
    [autoRows, upgrades],
  );
  // Group 2: already-resolved broad-type catch-alls (ppe / other-current-asset)
  // that Alfie can refine from the name (e.g. a NONCURRENT related-party loan
  // sitting in PP&E). Only rows that BOTH are refinable AND drew an upgrade —
  // everything else stays exactly as it sorted (zero PP&E regression).
  const refineRows = useMemo(
    () => (data?.chart ?? []).filter((r) => r.refinable && upgrades.has(r.accountId)),
    [data?.chart, upgrades],
  );
  const workPending = total > 0 || autoRows.length > 0 || refineRows.length > 0;

  // Name-suggested clusters: unanswered thin/coarse cards sharing one Alfie
  // suggestion, offered as a group (≥2) instead of dealt one card at a time —
  // e.g. 25 "Loan …" accounts all suggesting Loans & long-term debt. Conflicts
  // never cluster (suggested is always null there — nothing to rubber-stamp).
  const suggestionGroups = useMemo(() => {
    const byRole = new Map<string, { label: string; cards: XeroUnlockCard[] }>();
    for (const c of cards) {
      if (!c.suggested || answers[c.accountId] !== undefined) continue;
      const label = c.options.find((o) => o.role === c.suggested)?.label ?? c.suggested;
      if (!byRole.has(c.suggested)) byRole.set(c.suggested, { label, cards: [] });
      byRole.get(c.suggested)!.cards.push(c);
    }
    return [...byRole.entries()]
      .map(([role, g]) => ({ role, ...g }))
      .filter((g) => g.cards.length >= 2)
      .sort((a, b) => b.cards.length - a.cards.length);
  }, [cards, answers]);

  /** Stage a whole suggestion group as approved-as-a-group (undoable until Save). */
  const acceptSuggestionGroup = (g: { role: string; cards: XeroUnlockCard[] }) =>
    setAnswers((p) => {
      const next = { ...p };
      for (const c of g.cards) {
        next[c.accountId] = { role: g.role, signalHash: c.signalHash, state: 'bulk_accept' };
      }
      return next;
    });

  // If the current view's work drains (answers saved, group applied), fall back
  // to the grouped entry rather than a dead end.
  useEffect(() => {
    if (stage === 'ready' && view === 'cards' && total === 0) setView('groups');
  }, [stage, view, total]);

  const confirmations = useMemo<XeroMappingConfirmation[]>(
    () =>
      Object.entries(answers)
        /*
          THE NULL-ROLE FILTER IS GONE (Tim, 11 Oct).
          It silently discarded every "Got it — I'll fix it in Xero" acknowledgement: the card's
          terminal calls `onAnswer(null)`, this dropped it, the screen reported "Nothing to save
          here", and because the server's sticky gate needs a truthy role the same card came back
          on every classify for ever. The server accepts a role-less `deferred` — only a role-less
          `not_used` is rejected — so nothing here ever needed to filter. Answers now carry the
          group's catch-all anyway (`holdingRoleFor`), and anything still null is dropped by the
          map below rather than silently vanishing from the count.
        */
        .map(([id, a]) => ({
          accountId: id,
          role: a.role,
          confirmedState: a.state ?? ('deliberate' as const),
          signalHash: a.signalHash,
          ...settleFields(id, a.role),
        })),
    // `settleFields` carries the chart row and the proposals. Without it here the memo keeps the
    // snapshot from whenever `answers` last changed, so a refresh that turns a row into a conflict
    // or moves its reporting code would still post the old values.
    [answers, settleFields],
  );

  const saveAnswers = () => {
    // Narrate the movement, not just "saved" (advisor #11): a stateless
    // checklist shows positions; the toast is where the delta lives — so a
    // swap that moves the gap never reads as "nothing changed".
    const delta = stagedCoverageDelta(data?.chart ?? [], answers);
    confirm.mutate(confirmations, {
      onSuccess: () => {
        const parts: string[] = [];
        if (delta.covered.length) {
          parts.push(`${delta.covered.map(roleLabelShort).join(', ')} now covered`);
        }
        if (delta.emptied.length) {
          parts.push(
            `${delta.emptied.map(roleLabelShort).join(', ')} now empty — split the account in Xero if it's really both`,
          );
        }
        toast.success(parts.length ? `Saved — ${parts.join(' · ')}.` : 'Saved — Alfie updated your account roles.');
        setAnswers({});
        setIndex(0);
        refetch(); // re-classify so resolved accounts drop out of the flow
        // The mapping changed, so the stored role_totals + tie_out are now stale.
        // Re-pull the P&L to re-aggregate against the new mapping — otherwise the
        // dashboard's "tie-out confirmed" line would assert a match produced by
        // the OLD mapping. NOT fire-and-forget (advisor #5): a failed re-sync
        // would leave a ✓ checklist over figures aggregated under the old
        // mapping, so the failure must be loud and carry the recovery.
        sync.mutate({ reason: 'mapping' }, {
          onError: () =>
            toast.warning(
              "Your roles saved, but the figures couldn't re-pull — tap Refresh to bring them up to date.",
            ),
        });
      },
      onError: () => toast.error("Couldn't save your answers. Please try again."),
    });
  };

  const unstage = (accountId: string) =>
    setAnswers((p) => {
      const next = { ...p };
      delete next[accountId];
      return next;
    });

  // Apply the auto-resolve group: every row takes its type's landing role,
  // persisted as 'bulk_accept' (approved as a group, distinct from a deliberate
  // pick — trust reads the difference forever). Same congruence rule as a
  // manual save: the mapping changed, so the figures re-pull.
  const applyAutoResolve = () => {
    const confs: XeroMappingConfirmation[] = autoRows.map((r) => ({
      accountId: r.accountId,
      // Persist Alfie's refined role where it upgraded the catch-all default.
      role: proposedFor(r).role,
      confirmedState: 'bulk_accept' as const,
      signalHash: r.signalHash,
      // The role applied IS the proposal, so this resolves to false — computed rather than
      // hard-coded so the day a group applies something else, it stops lying.
      ...settleFields(r.accountId, proposedFor(r).role, proposedFor(r).role),
    }));
    if (!confs.length) return;
    confirm.mutate(confs, {
      onSuccess: () => {
        toast.success(
          `${confs.length} account${confs.length === 1 ? '' : 's'} auto-resolved from their Xero account types.`,
        );
        setAutoConsent(false);
        refetch();
        sync.mutate({ reason: 'mapping' }, {
          onError: () =>
            toast.warning(
              "Your roles saved, but the figures couldn't re-pull — tap Refresh to bring them up to date.",
            ),
        });
      },
      onError: () => toast.error("Couldn't apply the group. Please try again."),
    });
  };

  // Accept Alfie's name-refinement for one or more Group-2 rows (ppe/inventory
  // → a specific role). Same bulk_accept path; the human clicks to accept.
  const acceptRefinements = (rows: XeroChartRow[]) => {
    const confs = rows.reduce<XeroMappingConfirmation[]>((acc, r) => {
      const up = upgrades.get(r.accountId);
      if (up) {
        acc.push({
          accountId: r.accountId,
          role: up.role,
          confirmedState: 'bulk_accept',
          signalHash: r.signalHash,
          // Accepting Alfie's upgrade IS agreement with what was on screen.
          ...settleFields(r.accountId, up.role, up.role),
        });
      }
      return acc;
    }, []);
    if (!confs.length) return;
    confirm.mutate(confs, {
      onSuccess: () => {
        toast.success(
          `${confs.length} account${confs.length === 1 ? '' : 's'} refined from ${
            confs.length === 1 ? 'its name' : 'their names'
          }.`,
        );
        refetch();
        sync.mutate({ reason: 'mapping' }, {
          onError: () =>
            toast.warning(
              "Your roles saved, but the figures couldn't re-pull — tap Refresh to bring them up to date.",
            ),
        });
      },
      onError: () => toast.error("Couldn't apply the refinement. Please try again."),
    });
  };

  const answerCard = (role: string | null) => {
    if (!currentCard) return;
    setAnswers((p) => ({ ...p, [currentCard.accountId]: { role, signalHash: currentCard.signalHash } }));
    setDir(1);
    setIndex((i) => i + 1);
  };
  const back = () => {
    setDir(-1);
    setIndex((i) => Math.max(0, i - 1));
  };
  const editChart = (row: XeroChartRow, role: string | null) =>
    setAnswers((p) => ({ ...p, [row.accountId]: { role, signalHash: row.signalHash } }));

  const loading = stage === 'retrieval' || stage === 'classifying' || stage === 'ai';
  // THE WHOLE DIALOG LOCKS WHILE A REFRESH RUNS. Until now a refresh reset the
  // stage and nulled the data, so every `stage === 'ready'` block unmounted and
  // the screen went blank — which is also the only thing stopping a builder
  // saving into a classify that is already in flight (a late auto-write would
  // overwrite their answer). Keeping the view up removes that accidental guard,
  // so the lock has to be explicit. Ruled 22 Sep: lock all of it, not per
  // chapter, and do not design for a second tab.
  // The sync is fired by `saveAnswers` and NOT awaited, so leaving it out of the
  // lock drops the lock while it is still writing. A second chapter save would
  // then start a second sync, and whichever one read the mappings first but
  // finishes last puts the old buckets back on the dashboard — the builder sees
  // their answer take, then undo itself. Per-chapter saving is what makes that
  // race routine rather than rare, so the lock has to hold until the write ends.
  const locked = refreshing || confirm.isPending || sync.isPending;

  // Chapters (slice 5). Derived from the Xero account TYPE, so membership is
  // fixed before the builder answers anything and a chapter is genuinely
  // finishable. Recomputed as answers land so the rail's counts move with them.
  const chapters = useMemo(() => {
    if (!data) return [];
    // Overlay what the builder has answered but not yet saved. Counting the
    // server chart alone means the rail only moves when classify returns, so a
    // builder works through a chapter and watches "3 of 40" sit still. The
    // staged role is what they just chose; treat it as deliberate, because it is.
    const staged = data.chart.map((r) => {
      const a = answers[r.accountId];
      return a?.role
        ? { ...r, role: a.role, confirmedState: a.state ?? 'deliberate' }
        : r;
    });
    return buildChapters(staged);
  }, [data, answers]);
  const [chapterId, setChapterId] = useState<string | null>(null);
  // Default to the first chapter that still needs the builder, not the first
  // chapter — reopening the wizard should land where the work is.
  const activeChapterId = chapterId ??
    chapters.find((c) => c.outstanding > 0)?.meta.id ??
    chapters[0]?.meta.id ??
    null;
  const chapterRows = activeChapterId
    ? (chapters.find((c) => c.meta.id === activeChapterId)?.rows ?? [])
    : [];

  return (
    // Wider than the builder's ledger on purpose: this is a table to audit, not a question to
    // answer, and a bookkeeper is at a desk.
    <div className="mx-auto w-full max-w-5xl">
      {/* ---- THE ACCOUNTING VIEW -------------------------------------------------------------
          Tabs by classification, grouped by role, every group shut to begin with. This is the
          surface for this route; everything below it is the specialist tooling that has always
          lived here and has no other home — the contamination correction dialog, the
          blocked-metric fixer, the health audit, "About your books", the bank-purpose picker and
          the unplaced-transaction list. Five places in the cockpit link straight to those, so
          deleting them would break those links rather than tidy the page. */}
      {/* The finance nav every sibling view has, and a way back (Tim, 12 Oct 2026: "there's no
          back button from there"). */}
      <FinanceNav />
      <Link
        to="/finance"
        className="mt-2 inline-flex items-center gap-1 text-[12px] text-muted-foreground no-underline hover:text-foreground"
      >
        <ChevronLeft className="h-3.5 w-3.5" /> Dashboard
      </Link>
      <header className="mb-4 mt-3">
        <h1 className="ds-h1">Accounts</h1>
        <p className="mt-1 max-w-[70ch] text-sm text-muted-foreground">
          Every account, what it was set to, and who decided. Written for whoever keeps your
          books — a builder is asked the short version over the dashboard instead.
        </p>
      </header>

      <AccountingView
        rows={data?.chart ?? []}
        proposals={accountingProposals}
        busy={locked}
        onChangeRole={(accountId, role, signalHash) =>
          confirm.mutate([{
            accountId,
            role,
            confirmedState: 'deliberate',
            signalHash,
            ...settleFields(accountId, role),
          }])}
      />

      <div className="mx-auto mt-10 w-full max-w-2xl">
      {/* Locked = visible but inert. Nothing unmounts; the builder keeps reading
          the ledger while it recomputes underneath. */}
      <div
        aria-busy={locked}
        className={locked ? 'pointer-events-none select-none opacity-60 transition-opacity' : 'transition-opacity'}
      >
        {refreshing && (
          <p
            role="status"
            className="mb-3 flex items-center gap-2 text-sm text-muted-foreground"
          >
            <HearthstoneIcon size={14} />
            Recalculating with your answer — this stays on screen while it runs.
          </p>
        )}
        {/* Hero — paints immediately. Hidden once everything is classified: the
            completed dashboard carries its own organisation header. */}
        {!(stage === 'ready' && total === 0) && (
          <GlassPanel intensity="heavy" rounded="3xl" className="p-5 sm:p-6">
            <h1 className="ds-h1">
              Help Alfie unlock your metrics
            </h1>
            <p className="mt-1.5 text-sm text-muted-foreground">
              {loading
                ? 'Alfie is reading and sorting your Xero accounts…'
                : stage === 'error'
                ? "Alfie couldn't read your accounts."
                : `Alfie's mapped your ${data?.tenantName ?? 'Xero'} accounts.${
                  autoRows.length > 0
                    ? ` ${autoRows.length} can be auto-resolved with your say-so.`
                    : ''
                }${
                  total > 0
                    ? ` ${Math.max(0, total - answeredCount)} need your call before their metrics go live.`
                    : ''
                }`}
            </p>
          </GlassPanel>
        )}

        {/* Return-visit reconciliation — narrates what the sticky-role machinery
            did on a re-run (reconnect/revisit), so earlier work visibly survived
            and re-checking is explicit, never silent. First runs show nothing. */}
        {stage === 'ready' && data?.revisit && data.revisit.kept > 0 && (
          <GlassPanel intensity="medium" rounded="2xl" className="mt-4 flex items-start gap-2.5 px-4 py-3">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-success" aria-hidden />
            <p className="text-sm text-muted-foreground">
              <span className="text-foreground">Found your existing setup</span>
              {data.revisit.lastConfirmedAt && <> (confirmed {formatDateShort(data.revisit.lastConfirmedAt)})</>}
              {' — '}{data.revisit.kept} account{data.revisit.kept === 1 ? '' : 's'} unchanged and re-checked against Xero
              {data.revisit.reopened > 0 && (
                <>, <span className="text-warning">{data.revisit.reopened} changed in Xero since — asked again below</span></>
              )}
              {data.revisit.added > 0 && <>, {data.revisit.added} new</>}.
            </p>
          </GlassPanel>
        )}

        {/* Loading stages */}
        {loading && (
          <LoadingStages stage={stage} aiInputsCount={data ? data.aiInputs.length : null} />
        )}

        {stage === 'error' && (
          <GlassPanel className="mt-4 p-4">
            <p className="text-sm text-danger">Please try again in a moment.</p>
            <Button variant="outline" size="sm" className="mt-3" onClick={() => refetch()}>
              Retry
            </Button>
          </GlassPanel>
        )}

        {/* Ready — the grouped entry: what can be settled as a group, what
            needs a decision, and the full list. The initial state is grouped
            so 250 open accounts read as three choices, not a wall of cards. */}
        {stage === 'ready' && view === 'groups' && workPending && data && (
          <div className="mt-4 space-y-3">
            {autoRows.length > 0 && (
              <GlassPanel intensity="medium" rounded="2xl" className="p-4">
                <h3 className="ds-h3 flex items-center gap-2">
                  <HearthstoneIcon size={16} />
                  Can be auto-resolved
                  <span className="text-sm text-muted-foreground">· {autoRows.length}</span>
                </h3>
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                  These accounts carry no usable report-code detail, so Alfie settles each one with
                  the standard role for its Xero account type. They stay labelled "approved as a
                  group", any of them can be refined later, and if an account changes in Xero it
                  reopens automatically.
                </p>
                {refinedCount > 0 && (
                  <p className="mt-2 flex items-start gap-1.5 text-xs text-info">
                    <HearthstoneIcon size={12} className="mt-0.5 shrink-0" />
                    <span>
                      Alfie refined {refinedCount} of these from their names — e.g.{' '}
                      {[
                        ...new Set(
                          autoRows
                            .filter((r) => upgrades.has(r.accountId))
                            .map((r) => proposedFor(r).label),
                        ),
                      ].slice(0, 3).join(', ')} — instead of the generic bucket.
                    </span>
                  </p>
                )}
                <p className="mt-2 text-xs text-muted-foreground">
                  {Object.entries(
                    autoRows.reduce<Record<string, number>>((acc, r) => {
                      const k = proposedFor(r).label;
                      acc[k] = (acc[k] ?? 0) + 1;
                      return acc;
                    }, {}),
                  ).map(([k, n]) => `${n} × ${k}`).join(' · ')}
                </p>
                <label className="mt-3 flex min-h-[44px] cursor-pointer items-start gap-2.5">
                  <input
                    type="checkbox"
                    checked={autoConsent}
                    onChange={(e) => setAutoConsent(e.target.checked)}
                    className="mt-0.5 h-5 w-5"
                  />
                  <span className="text-sm text-foreground">
                    Assign these {autoRows.length} accounts their standard roles
                  </span>
                </label>
                <div className="mt-2 flex flex-wrap items-center gap-3">
                  <Button
                    className="min-h-[44px]"
                    disabled={!autoConsent || confirm.isPending}
                    onClick={applyAutoResolve}
                  >
                    {confirm.isPending
                      ? 'Applying…'
                      : `Apply to ${autoRows.length} account${autoRows.length === 1 ? '' : 's'}`}
                  </Button>
                  <button
                    type="button"
                    onClick={() => {
                      setTableFilter('auto');
                      setView('table');
                    }}
                    className="min-h-[44px] py-2 text-sm text-primary hover:underline"
                  >
                    See the list first
                  </button>
                </div>
              </GlassPanel>
            )}

            {refineRows.length > 0 && (
              <GlassPanel intensity="medium" rounded="2xl" className="p-4">
                <h3 className="ds-h3 flex items-center gap-2">
                  <HearthstoneIcon size={16} />
                  Alfie can refine these from their names
                  <span className="text-sm text-muted-foreground">· {refineRows.length}</span>
                </h3>
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                  These sorted fine on their Xero account type, but the name suggests something more
                  specific — e.g. a loan or related-party balance sitting in plant &amp; equipment.
                  Accepting recodes the role in Alfie only; nothing changes in Xero, and each reopens
                  automatically if the account changes there.
                </p>
                <ul className="mt-3 space-y-1.5">
                  {refineRows.map((r) => {
                    const up = upgrades.get(r.accountId)!;
                    return (
                      <li
                        key={r.accountId}
                        className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm"
                      >
                        <span className="min-w-0 flex-1 truncate text-foreground">
                          <span className="text-muted-foreground">{r.code}</span> {r.name}
                        </span>
                        <span className="inline-flex items-center gap-1.5 text-xs">
                          <span className="text-muted-foreground line-through">{r.roleLabel}</span>
                          <ArrowRight className="h-3 w-3 text-info" aria-hidden />
                          <span className="text-info">{up.roleLabel}</span>
                        </span>
                        <Button
                          variant="outline"
                          className="min-h-[36px] shrink-0 px-3 py-1 text-xs"
                          disabled={confirm.isPending}
                          onClick={() => acceptRefinements([r])}
                        >
                          Accept
                        </Button>
                      </li>
                    );
                  })}
                </ul>
                {refineRows.length > 1 && (
                  <div className="mt-3">
                    <Button
                      className="min-h-[44px]"
                      disabled={confirm.isPending}
                      onClick={() => acceptRefinements(refineRows)}
                    >
                      {confirm.isPending
                        ? 'Applying…'
                        : `Accept all ${refineRows.length} refinements`}
                    </Button>
                  </div>
                )}
              </GlassPanel>
            )}

            {total > 0 && (
              <GlassPanel intensity="medium" rounded="2xl" className="p-4">
                <h3 className="ds-h3 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-warning" aria-hidden />
                  Needs your decision
                  <span className="text-sm text-muted-foreground">· {total - answeredCount}</span>
                </h3>
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                  Mixed signals, or no standard role exists — Alfie won&apos;t guess these without
                  you.
                </p>

                {/* Name-suggested clusters — one call for a whole family of
                    accounts (e.g. 25 loan accounts), instead of 25 cards. */}
                {suggestionGroups.map((g) => (
                  <SuggestionGroupRow
                    key={g.role}
                    group={g}
                    chart={data.chart}
                    onAccept={() => acceptSuggestionGroup(g)}
                  />
                ))}

                {total - answeredCount > 0 && (
                  <Button
                    variant="outline"
                    className="mt-3 min-h-[44px]"
                    onClick={() => setView('cards')}
                  >
                    {suggestionGroups.length > 0 ? 'Review one by one' : `Review ${
                      total - answeredCount
                    } account${total - answeredCount === 1 ? '' : 's'}`}
                    <ArrowRight className="ml-1.5 h-4 w-4" aria-hidden />
                  </Button>
                )}
              </GlassPanel>
            )}

            <GlassPanel intensity="medium" rounded="2xl" className="p-4">
              <h3 className="ds-h3 flex items-center gap-2">
                <Layers className="h-4 w-4 text-muted-foreground" aria-hidden />
                All accounts
                <span className="text-sm text-muted-foreground">· {data.chart.length}</span>
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                {data.chart.length - autoRows.length - total} sorted
                {autoRows.length > 0 && ` · ${autoRows.length} auto-resolvable`}
                {total > 0 && ` · ${total} need you`}
                {' — every assignment at a glance, editable.'}
              </p>
              <Button
                variant="outline"
                className="mt-3 min-h-[44px]"
                onClick={() => {
                  setTableFilter('all');
                  setView('table');
                }}
              >
                Open the full list
              </Button>
            </GlassPanel>
          </div>
        )}

        {/* The full list — filterable, sectioned by classification. */}
        {stage === 'ready' && view === 'table' && data && (
          <>
            <div className="mt-4 flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={() => setView('groups')}
                className="inline-flex min-h-[44px] items-center gap-1 py-2 text-sm text-muted-foreground hover:text-foreground"
              >
                <ChevronLeft className="h-4 w-4" aria-hidden /> Back
              </button>
            </div>
            <div className="flex min-w-0 items-center gap-2 overflow-x-auto scrollbar-hide">
              {STATUS_FILTERS.map((f) => {
                const count = f.id === 'all'
                  ? data.chart.length
                  : data.chart.filter((r) => rowStatusOf(r, answers) === f.id).length;
                return (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => setTableFilter(f.id)}
                    className={cn(
                      'min-h-[36px] shrink-0 whitespace-nowrap rounded-full border px-3.5 py-1.5 text-xs transition-colors',
                      tableFilter === f.id
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border bg-card/60 text-foreground hover:border-primary/40',
                    )}
                  >
                    {f.label} · {count}
                  </button>
                );
              })}
            </div>
            {/* Ledger left, chapters right. The rail is on the right so Alfie can
                live under it — the column that says where you are is the column
                you ask about it in. Stacks under the ledger on a phone. */}
            <div className="mt-3 flex flex-col gap-4 md:flex-row md:items-start">
              <div className="min-w-0 flex-1">
                <SectionedChart
                  chart={chapterRows.length > 0 ? chapterRows : data.chart}
                  answers={answers}
                  onEdit={editChart}
                  filter={tableFilter}
                  shortCode={activeShortCode}
                  upgrades={upgrades}
                />
              </div>
              {chapters.length > 1 && (
                <aside className="w-full shrink-0 rounded-xl border border-border bg-muted/30 py-3 md:w-[240px]">
                  <ChapterRail
                    chapters={chapters}
                    currentId={activeChapterId ?? ''}
                    onSelect={setChapterId}
                    locked={locked}
                  />
                </aside>
              )}
            </div>
          </>
        )}

        {/* Ready — the one-at-a-time question flow */}
        {stage === 'ready' && view === 'cards' && total > 0 && (
          <>
            <button
              type="button"
              onClick={() => setView('groups')}
              className="mt-4 inline-flex min-h-[44px] items-center gap-1 py-2 text-sm text-muted-foreground hover:text-foreground"
            >
              <ChevronLeft className="h-4 w-4" aria-hidden /> Back to groups
            </button>
            {/* Progress */}
            <div className="flex items-center gap-3 px-1">
              <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-card">
                <div
                  className="h-full rounded-full bg-primary transition-all"
                  style={{ width: `${(answeredCount / total) * 100}%` }}
                />
              </div>
              <span className="shrink-0 text-xs text-muted-foreground">
                {answeredCount} / {total}
              </span>
            </div>

            <div className="mt-3 min-h-[220px]">
              <AnimatePresence mode="wait" custom={dir}>
                {currentCard
                  ? (
                    <motion.div
                      key={currentCard.accountId}
                      custom={dir}
                      initial={{ opacity: 0, x: dir * 80 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: dir * -80 }}
                      transition={{ duration: 0.22 }}
                    >
                      <UnlockCardView
                        card={currentCard}
                        answer={answers[currentCard.accountId]}
                        onAnswer={answerCard}
                        shortCode={activeShortCode}
                      />
                      {index > 0 && (
                        <button
                          type="button"
                          onClick={back}
                          className="mt-2 inline-flex min-h-[40px] items-center gap-1 py-2 text-xs text-muted-foreground hover:text-foreground"
                        >
                          <ChevronLeft className="h-3.5 w-3.5" /> Back
                        </button>
                      )}
                    </motion.div>
                  )
                  : (
                    <motion.div
                      key="done"
                      initial={{ opacity: 0, x: 80 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.22 }}
                    >
                      <GlassPanel intensity="medium" rounded="2xl" className="p-6 text-center">
                        <CheckCircle2 className="mx-auto h-8 w-8 text-success" />
                        <p className="mt-2 text-foreground">That's everything Alfie needed.</p>
                        <p className="mt-1 text-sm text-muted-foreground">
                          {confirmations.length > 0
                            ? 'Save your answers to unlock the metrics.'
                            : "Nothing to save here — these accounts need a fix in Xero, not a role. Your other accounts are already mapped."}
                        </p>
                        {/* When there's nothing to persist (all remaining cards
                            were conflicts routed to Xero), the sticky save bar
                            never appears — so the done screen must carry its own
                            way forward, or the builder is stranded here. */}
                        {confirmations.length === 0 && (
                          <div className="mt-4 flex flex-wrap justify-center gap-3">
                            <Link
                              to="/finance"
                              className="inline-flex min-h-[44px] items-center gap-2 rounded-2xl bg-primary px-5 text-primary-foreground transition-colors hover:bg-primary/90"
                            >
                              <BarChart3 className="h-4 w-4" aria-hidden />
                              Go to your dashboard
                            </Link>
                            <button
                              type="button"
                              onClick={() => setView('groups')}
                              className="inline-flex min-h-[44px] items-center gap-1 rounded-2xl border border-border px-4 text-sm text-foreground hover:border-primary/40"
                            >
                              Back to accounts
                            </button>
                          </div>
                        )}
                      </GlassPanel>
                    </motion.div>
                  )}
              </AnimatePresence>
            </div>
          </>
        )}

        {/* Trust footer — working views only; the dashboard has its own */}
        {stage === 'ready' && workPending && (
          <div className="mt-5 flex items-center gap-2 px-1 text-xs text-muted-foreground">
            <ShieldCheck className="h-4 w-4 text-success" aria-hidden />
            Your figures come straight from Xero.
          </div>
        )}

        {/* Completed — nothing left to classify (and no group awaiting the
            builder's approval) → the finance landing */}
        {stage === 'ready' && !workPending && data && (
          <CompletedDashboard
            tenantName={data.tenantName}
            snapshot={status.data?.latestSnapshot ?? null}
            snapshotLoading={status.isLoading}
            chart={data.chart}
            coverage={data.coverage}
            chartDrift={status.data?.connections.find((c) => c.isActive)?.chartDrift ?? null}
            health={data.health ??
              status.data?.connections.find((c) => c.isActive)?.health ?? null}
            healthInputs={status.data?.connections.find((c) => c.isActive)?.healthInputs ?? null}
            healthAcks={status.data?.connections.find((c) => c.isActive)?.healthAcks ?? null}
            healthDeclarations={status.data?.connections.find((c) => c.isActive)?.healthDeclarations ?? null}
            cashDesignations={data.cashDesignations ?? {}}
            aliases={data.aliases ?? []}
            unplacedDispositions={data.unplacedDispositions ?? []}
            missedCodes={(() => {
              const fa = status.data?.connections.find((c) => c.isActive)?.flowAggregates as
                | { guards?: { missedCodes?: Array<{ code: string; count: number; volume: number; net: number }> } }
                | null
                | undefined;
              return fa?.guards?.missedCodes ?? [];
            })()}
            organizationId={organizationId ?? ''}
            shortCode={activeShortCode}
            focusFix={fix ?? null}
            onConsumeFix={() => void navigate({ search: (p) => ({ ...p, fix: undefined }), replace: true })}
            focusAccount={fixAccount ?? null}
            onConsumeFixAccount={() => void navigate({ search: (p) => ({ ...p, fixAccount: undefined }), replace: true })}
            focusAudit={audit === true}
            answers={answers}
            onEdit={editChart}
            onUnstage={unstage}
            onRefresh={() => sync.mutate(undefined)}
            syncing={sync.isPending}
            onDisconnect={(deleteMappings) =>
              disconnect.mutate({ deleteMappings }, {
                onSuccess: (res) => {
                  if (res.mappingsDeleted > 0) {
                    toast.success(`Disconnected — ${res.mappingsDeleted} saved classifications deleted.`);
                  } else if (res.mappingsSkipped) {
                    toast.info('Disconnected. Setup kept — another Xero connection still uses it.');
                  } else {
                    toast.success('Disconnected — your saved setup is kept for a reconnect.');
                  }
                },
              })}
            disconnecting={disconnect.isPending}
            canDisconnect={isAdminOrOwner}
          />
        )}

        {/* Save bar — sticky within the scroll column (no fixed positioning, so
            it never collides with the shell), clearing the home indicator. */}
        {confirmations.length > 0 && (
          <div data-alfie-avoid className="sticky bottom-0 z-10 mt-5 pb-[env(safe-area-inset-bottom)]">
            <div className="flex items-center justify-between gap-3 rounded-2xl border border-border bg-card/85 px-4 py-3 shadow-lg backdrop-blur-xl">
              <span className="text-sm text-muted-foreground">{confirmations.length} to save</span>
              <Button
                onClick={saveAnswers}
                disabled={confirm.isPending}
                className="min-h-[44px]"
              >
                {confirm.isPending
                  ? 'Saving…'
                  : (
                    <>
                      <Check className="mr-1.5 h-4 w-4" /> Save answers
                    </>
                  )}
              </Button>
            </div>
          </div>
        )}
      </div>
      </div>
    </div>
  );
}
